import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import './snooker.css';
import './snookerGameOverModal.css';



import {
  ref,
  get,
  set,
  update,
  push,
  onValue,
  runTransaction,
  serverTimestamp
} from 'firebase/database';

import {database as rtdb } from '../../database/firebase';
import { sendReport } from '../functions/sendReport';

// Notify backend when Snooker computer game starts (like Ludo)
const notifySnookerLiveGameStart = async (gameId, playersPayload) => {
  try {
    const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null;
    const userData = stored ? JSON.parse(stored) : {};
    await fetch(process.env.REACT_APP_FOLDER_LINK + '/api/save-computer-multiplayer.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: userData.sessionId || null,
        gameId,
        gameType: 'snooker',
        status: 'initiated',
        players: playersPayload || [{ userId: userData.userId || null, name: 'Player 1' }, { name: 'Computer' }],
        phoneNumber: userData.phoneNumber || null,
      }),
    });
  } catch (err) {
    console.error('Failed to notify Snooker computer game start', err);
  }
};

// Notify backend when Snooker live/computer game ends (like Ludo) + ExtraMile report when send_report
const notifySnookerLiveGameEnd = async (gameId, winnerId, outcome) => {
  try {
    const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null;
    const userData = stored ? JSON.parse(stored) : {};
    const sessionId = userData.sessionId || null;

    const res = await fetch(process.env.REACT_APP_FOLDER_LINK + '/api/save-computer-multiplayer.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        gameId,
        gameType: 'snooker',
        status: 'completed',
        outcome,
        winnerId,
      }),
    });
    const data = res.ok ? await res.json().catch(() => ({})) : {};
    if (data.send_report === true) {
      const reportData = {
        sessionId: userData.sessionId,
        role: userData.source,
        token: userData.token || null,
        name: userData.name,
        userId: userData.userId,
        gameId: userData.gameId || '',
        organizationId: userData.organizationId,
        points:
          winnerId && userData.userId && String(winnerId) === String(userData.userId)
            ? 10
            : 0,
        time: data.duration || '00:00:00',
        reportId: userData.reportId || null,
        ans: '',
      };
      sendReport(reportData)
        .then((apiRes) => console.log('Snooker Report API SUCCESS:', apiRes))
        .catch((err) => console.error('Snooker Report API FAILED:', err));
    }
  } catch (err) {
    console.error('Failed to notify Snooker computer game end', err);
  }
};

// Resolve tournament seat color (black/white) for the frame winner
const resolveWinnerSeatColor = (winnerPlayer, localPlayerNumber, mySeatColor) => {
  const normalized = String(mySeatColor || '').toLowerCase();
  if (normalized !== 'black' && normalized !== 'white') {
    return winnerPlayer === 1 ? 'player1' : 'player2';
  }
  const other = normalized === 'black' ? 'white' : 'black';
  return winnerPlayer === localPlayerNumber ? normalized : other;
};

const markTournamentPlayerPresentApi = async (gameId, userId) => {
  if (!gameId || !userId) return;
  try {
    await fetch(process.env.REACT_APP_FOLDER_LINK + '/api/setPlayerPresent.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gameId, userId }),
    });
  } catch (err) {
    console.error('Failed to mark Snooker player present', err);
  }
};

// Notify backend save-winner.php when match ends in multiplayer (same as Ludo)
const notifySnookerWinnerApi = async (gameId, winnerSeatColor, mode, outcome = 'win', urlTournamentId = null, options = {}) => {
  const { immediateRedirect = false, skipRedirect = false } = typeof options === 'boolean'
    ? { immediateRedirect: options }
    : options;
  if (!gameId || !winnerSeatColor) return;
  try {
    const body = { gameId, color: winnerSeatColor, mode, outcome, gameType: 'snooker' };
    if (mode === 'live' && typeof sessionStorage !== 'undefined') {
      const stored = sessionStorage.getItem('userData');
      if (stored) {
        try {
          body.userData = JSON.parse(stored);
        } catch (e) {}
      }
    }
    const res = await fetch(process.env.REACT_APP_FOLDER_LINK + '/api/save-winner.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    let data;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }
    const success = (data && data.response && data.response.success) ?? data?.success;
    const tournamentId =
      (data && data.response && data.response.tournamentId) ??
      data?.tournamentId ??
      urlTournamentId;
    if (!skipRedirect && mode === 'live') {
      if (immediateRedirect) {
        window.location.href = '/tournament-page';
      } else {
        setTimeout(() => { window.location.href = '/tournament-page'; }, 3000);
      }
    } else if (!skipRedirect && success && tournamentId) {
      if (immediateRedirect) {
        window.location.href = `/tournament-structure/${tournamentId}`;
      } else {
        setTimeout(() => { window.location.href = `/tournament-structure/${tournamentId}`; }, 3000);
      }
    } else if (!skipRedirect && immediateRedirect && urlTournamentId) {
      window.location.href = `/tournament-structure/${urlTournamentId}`;
    }
  } catch (err) {
    console.error('Failed to notify Snooker winner API', err);
  }
};
// ============================================================================
// CONSTANTS
// ============================================================================

const TABLE_WIDTH = 1200;
const TABLE_HEIGHT = 600;
const BALL_RADIUS = 10;
const FRICTION = 0.99;
const MIN_VELOCITY = 0.012;
const CUE_BALL_VELOCITY_SCALE = 0.78;
const CUE_BALL_FRICTION = 0.97;
const SEPARATION_EPSILON = 0.5;
const COLLISION_PASSES = 6;
const RESTITUTION = 0.9;
/** Min relative speed along contact normal (b1→b2) to apply impulse — approaching is (v1−v2)·n > 0 */
const MIN_COLLISION_APPROACH = 0.001;
/** Looser bound for first-contact typing only (reduces null/wrong lock-in); not used for collision impulses */
const FIRST_CONTACT_APPROACH_RELAXED = -0.04;
const PHYSICS_SUBSTEPS = 3;
const MAX_BALL_SPEED = 25;
/** Visual pocket hole on canvas */
const POCKET_RADIUS = 20;
/** Gameplay: ball center within this distance pots (wider than visual mouth) */
const POCKET_CAPTURE_RADIUS = 34;
/** Skip cushion bounce when this close to a pocket on that cushion */
const POCKET_MOUTH_DIST = 54;
/** Move only middle pockets outward toward rail. */
const MIDDLE_POCKET_BACK_OFFSET = 6;
const TABLE_MARKING_COLOR = 'rgba(255,255,255,0.9)';
const TABLE_MARKING_WIDTH = 1.5;
const COLOR_RESPOT_DELAY_MS = 520;
/** Table-centre toasts (foul + score); keep in sync with `foulPopupFade` / `scorePopupFade` in snooker.css */
const TABLE_TOAST_MS = 5000;
const CUE_OVERLAY_MARGIN = 280;
const CUE_STICK_MAX_LENGTH = 500;
/** Power readout cap — matches the Math.min(power, 10) already used for the numeric display. */
const MAX_SHOT_POWER = 10;
/** Below this, handlePointerUp treats the drag as a no-op (no shot fired) — kept in sync with that threshold for the meter. */
const MIN_SHOT_POWER = 0.5;
/** Smallest length the stick is still clamped down to near a rail/pocket, so it never fully disappears. */
const CUE_STICK_MIN_LENGTH = 28;
const CUE_START_X_OFFSET = BALL_RADIUS * 5;
const CUE_START_Y_OFFSET = BALL_RADIUS * 4;
const BASE_FRAME_MS = 1000 / 60;
const SYNC_MOVING_MS = 16;
const SYNC_IDLE_MS = 500;
const TABLE_PADDING = 0;
// Outer dark frame thickness (the felt playable area is based on INNER_EDGE).
// Reducing this removes the extra internal border visuals and increases usable space.
const FRAME_WIDTH = 1;
const RAIL_WIDTH = 14;
const INNER_EDGE = FRAME_WIDTH + RAIL_WIDTH;
const SHOT_TIME_LIMIT = 30;
const MAX_MISSED_TURNS = 3;
const SHOT_TIMER_TICK_MS = 250;
const LOBBY_WAIT_SECONDS = 600;
const PRESENCE_UPDATE_MS = 8000;
const PRESENCE_TIMEOUT_MS = 25000;
const ROOM_CODE_LENGTH = 6;
const ROOM_CODE_MAX_URL_LENGTH = 128; // tournament/gameId from backend can be longer

// ---------------------------------------------------------------------------
// Tournament waiting overlay copy
// ---------------------------------------------------------------------------

/** Top line — advances once per elapsed minute of the 10-minute join window. */
const WAITING_MINUTE_MESSAGES = [
  "You're here. Your opponent's entrance music is still loading.",
  'Two minutes in. Still a solo act. Stay tuned.',
  'Three minutes down. Your opponent is keeping the suspense alive.',
  'Still waiting. Mentally warming up counts, right?',
  'Halfway through the wait. Fashionably late has its limits.',
  "Six minutes in. Your opponent's dramatic entrance is getting very dramatic.",
  'Three minutes left. A good time for your opponent to appear.',
  'Two minutes left. This entrance could use a fast forward button.',
  "One minute left. It's almost now or never.",
  'Final countdown! No opponent by zero? Hello, next round.',
];

/** Bottom line — cycles every 15 seconds. */
const WAITING_ROTATING_MESSAGES = [
  'Two can play this game. Just waiting for the second.',
  'Your opponent has 10 minutes to join. Dramatic entrances take time.',
  "Stay here. We'd hate for you both to take turns waiting.",
  "That countdown? Your opponent's late meter.",
  'No opponent when the timer hits zero? You win and head straight to the next round.',
];

const WAITING_ROTATE_INTERVAL_SECONDS = 15;

/**
 * Full-screen waiting overlay shown while a tournament player waits for their
 * opponent. Purely presentational: all values derive from `secondsLeft`, which
 * is owned by the existing lobby timer.
 */
const SnookerWaitingOverlay = ({ secondsLeft }) => {
  const clamped = Math.max(0, Math.min(LOBBY_WAIT_SECONDS, Number(secondsLeft) || 0));
  const elapsed = LOBBY_WAIT_SECONDS - clamped;

  const minuteIndex = Math.min(
    WAITING_MINUTE_MESSAGES.length - 1,
    Math.floor(elapsed / 60)
  );
  const rotatingIndex =
    Math.floor(elapsed / WAITING_ROTATE_INTERVAL_SECONDS) % WAITING_ROTATING_MESSAGES.length;

  const minutes = String(Math.floor(clamped / 60)).padStart(2, '0');
  const seconds = String(clamped % 60).padStart(2, '0');

  return (
    <div className="snooker-waiting-overlay" role="status" aria-live="polite">
      <div className="snooker-waiting-overlay__inner">
        <p className="snooker-waiting-overlay__minute-message">
          {WAITING_MINUTE_MESSAGES[minuteIndex]}
        </p>
        <div className="snooker-waiting-overlay__countdown" aria-label={`${minutes} minutes ${seconds} seconds remaining`}>
          {minutes}:{seconds}
        </div>
        <p className="snooker-waiting-overlay__rotating-message">
          {WAITING_ROTATING_MESSAGES[rotatingIndex]}
        </p>
      </div>
    </div>
  );
};

// Ball colors
const COLORS = {
  WHITE: '#FFFFFF',
  RED: '#DC143C',
  YELLOW: '#FFD700',
  GREEN: '#228B22',
  BROWN: '#8B4513',
  BLUE: '#0000FF',
  PINK: '#FF69B4',
  BLACK: '#000000',
  TABLE: '#0B5D1E',
  CUSHION: '#8B4513'
};

// Ball values for scoring
const BALL_VALUES = {
  RED: 1,
  YELLOW: 2,
  GREEN: 3,
  BROWN: 4,
  BLUE: 5,
  PINK: 6,
  BLACK: 7
};

// Spot positions on table (in logical coordinates)
const SPOTS = {
  YELLOW: { x: TABLE_WIDTH * 0.25, y: TABLE_HEIGHT * 0.5 },
  GREEN: { x: TABLE_WIDTH * 0.25, y: TABLE_HEIGHT * 0.25 },
  BROWN: { x: TABLE_WIDTH * 0.25, y: TABLE_HEIGHT * 0.75 },
  BLUE: { x: TABLE_WIDTH * 0.5, y: TABLE_HEIGHT * 0.5 },
  PINK: { x: TABLE_WIDTH * 0.75, y: TABLE_HEIGHT * 0.5 },
  BLACK: { x: TABLE_WIDTH * 0.875, y: TABLE_HEIGHT * 0.5 }
};

// Start the cue ball just behind the yellow and a little off the centre line
// so the opening shot has a cleaner lane to the object balls.
const CUE_START = {
  x: SPOTS.YELLOW.x - CUE_START_X_OFFSET,
  y: SPOTS.YELLOW.y + CUE_START_Y_OFFSET
};

// Pocket positions (inner edge of rail: corners and mid top/bottom)
const POCKETS = [
  { x: INNER_EDGE, y: INNER_EDGE },                                      // Top-left
  { x: TABLE_WIDTH / 2, y: INNER_EDGE - MIDDLE_POCKET_BACK_OFFSET },     // Top-middle
  { x: TABLE_WIDTH - INNER_EDGE, y: INNER_EDGE },                        // Top-right
  { x: INNER_EDGE, y: TABLE_HEIGHT - INNER_EDGE },                       // Bottom-left
  { x: TABLE_WIDTH / 2, y: TABLE_HEIGHT - INNER_EDGE + MIDDLE_POCKET_BACK_OFFSET }, // Bottom-middle
  { x: TABLE_WIDTH - INNER_EDGE, y: TABLE_HEIGHT - INNER_EDGE }         // Bottom-right
];

const POCKETS_BY_CUSHION = {
  left: [0, 3],
  right: [2, 5],
  top: [0, 1, 2],
  bottom: [3, 4, 5]
};

function isBallInPocketMouthForCushion(bx, by, cushionSide) {
  const idxs = POCKETS_BY_CUSHION[cushionSide];
  for (let i = 0; i < idxs.length; i++) {
    const p = POCKETS[idxs[i]];
    if (Math.hypot(bx - p.x, by - p.y) < POCKET_MOUTH_DIST) return true;
  }
  return false;
}

function nearestPocket(x, y) {
  let best = POCKETS[0];
  let bestD = Infinity;
  for (let i = 0; i < POCKETS.length; i++) {
    const p = POCKETS[i];
    const d = (x - p.x) ** 2 + (y - p.y) ** 2;
    if (d < bestD) { bestD = d; best = p; }
  }
  return best;
}

/** Human-comparable AI power (same scale as pull distance / 25 in handlePointerUp). */
function aiPowerFromDistance(dist) {
  const d = Math.max(1, dist);
  let p = 0.32 * Math.sqrt(d);
  if (d > 280) p += 0.45;
  if (d > 520) p += 0.35;
  return Math.min(9, Math.max(3, p));
}

function aiGhostShotBlocked(whiteX, whiteY, gx, gy, ignoreIds, balls) {
  const dx = gx - whiteX;
  const dy = gy - whiteY;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  for (let i = 0; i < balls.length; i++) {
    const b = balls[i];
    if (!b.active || ignoreIds.has(b.id)) continue;
    const t = (b.x - whiteX) * ux + (b.y - whiteY) * uy;
    if (t < BALL_RADIUS * 1.5 || t > len - BALL_RADIUS * 1.5) continue;
    const px = whiteX + t * ux;
    const py = whiteY + t * uy;
    if (Math.hypot(b.x - px, b.y - py) < BALL_RADIUS * 2.05) return true;
  }
  return false;
}

// ============================================================================
// HELPERS
// ============================================================================

function countActiveObjectBalls(balls) {
  let n = 0;
  for (let i = 0; i < balls.length; i++) {
    const b = balls[i];
    if (b.active && b.type !== 'WHITE') n += 1;
  }
  return n;
}

function countActiveReds(balls) {
  let n = 0;
  for (let i = 0; i < balls.length; i++) {
    const b = balls[i];
    if (b.active && b.type === 'RED') n += 1;
  }
  return n;
}

function nextColorRequiredOnTable(balls, currentRequired = null) {
  const order = ['YELLOW', 'GREEN', 'BROWN', 'BLUE', 'PINK', 'BLACK'];
  const active = new Set(balls.filter((b) => b.active).map((b) => b.type));
  if (currentRequired && active.has(currentRequired)) return currentRequired;
  for (let i = 0; i < order.length; i++) {
    if (active.has(order[i])) return order[i];
  }
  return 'YELLOW';
}

/** Keep active balls strictly inside playable cushion bounds. */
function clampActiveBallToPlayArea(ball) {
  if (!ball.active) return;
  const minX = INNER_EDGE + BALL_RADIUS;
  const maxX = TABLE_WIDTH - INNER_EDGE - BALL_RADIUS;
  const minY = INNER_EDGE + BALL_RADIUS;
  const maxY = TABLE_HEIGHT - INNER_EDGE - BALL_RADIUS;
  ball.x = Math.min(Math.max(ball.x, minX), maxX);
  ball.y = Math.min(Math.max(ball.y, minY), maxY);
}

/**
 * Shortest distance from point P to segment AB.
 * Used for swept pocket capture: `checkPockets` only samples positions once per
 * frame, so a fast ball can move further in one frame than POCKET_CAPTURE_RADIUS
 * and land outside the capture window on both sides of a pocket. Testing the
 * travel segment instead of the end point closes that gap.
 */
function pointToSegmentDistance(px, py, ax, ay, bx, by) {
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const abLenSq = abx * abx + aby * aby;
  if (abLenSq === 0) return Math.hypot(apx, apy);
  let t = (apx * abx + apy * aby) / abLenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * abx), py - (ay + t * aby));
}

/**
 * Max per-frame travel treated as real motion for swept capture.
 * Real travel peaks near ~60px/frame (MAX_BALL_SPEED 25 at the 40ms delta cap),
 * so anything beyond this is a respot/teleport and must not sweep through pockets.
 */
const MAX_SWEPT_SEGMENT = 140;

const RESPOST_MIN_SEP = BALL_RADIUS * 2 + 0.75;

function findRespotPosition(spotX, spotY, ballsArray) {
  const minX = INNER_EDGE + BALL_RADIUS;
  const maxX = TABLE_WIDTH - INNER_EDGE - BALL_RADIUS;
  const minY = INNER_EDGE + BALL_RADIUS;
  const maxY = TABLE_HEIGHT - INNER_EDGE - BALL_RADIUS;
  function isClear(px, py) {
    for (let i = 0; i < ballsArray.length; i++) {
      const b = ballsArray[i];
      if (!b.active) continue;
      if (Math.hypot(b.x - px, b.y - py) < RESPOST_MIN_SEP) return false;
    }
    return true;
  }
  if (isClear(spotX, spotY)) return { x: spotX, y: spotY };
  for (let step = 3; step <= 120; step += 2) {
    for (let k = 0; k < 8; k++) {
      const ang = (k * Math.PI) / 4;
      let px = spotX + Math.cos(ang) * step;
      let py = spotY + Math.sin(ang) * step;
      px = Math.min(Math.max(px, minX), maxX);
      py = Math.min(Math.max(py, minY), maxY);
      if (isClear(px, py)) return { x: px, y: py };
    }
  }
  return { x: Math.min(Math.max(spotX, minX), maxX), y: Math.min(Math.max(spotY, minY), maxY) };
}

function pickBestFirstContactFromCandidates(cands) {
  if (cands.length === 0) return null;
  let best = cands[0];
  for (let k = 1; k < cands.length; k++) {
    const c = cands[k];
    if (c.entry < best.entry - 1e-9 || (Math.abs(c.entry - best.entry) <= 1e-9 && c.dist < best.dist)) best = c;
  }
  return { type: best.type };
}

/**
 * Predict the first object ball reachable from the cue's initial straight-line path.
 */
function predictFirstCueContactFromShotLine(whiteStart, cueUnit, balls) {
  if (!whiteStart || !cueUnit) return null;
  const contactRadius = BALL_RADIUS * 2;
  const contactRadiusSq = contactRadius * contactRadius;
  const cands = [];
  for (let i = 0; i < balls.length; i++) {
    const b = balls[i];
    if (!b.active || b.type === 'WHITE') continue;
    const dx = b.x - whiteStart.x;
    const dy = b.y - whiteStart.y;
    const proj = dx * cueUnit.ux + dy * cueUnit.uy;
    if (proj < -1e-8) continue;
    const perpSq = dx * dx + dy * dy - proj * proj;
    if (!Number.isFinite(perpSq) || perpSq > contactRadiusSq) continue;
    const reach = Math.sqrt(Math.max(0, contactRadiusSq - perpSq));
    const entry = Math.max(0, proj - reach);
    cands.push({ type: b.type, entry, dist: Math.hypot(dx, dy) });
  }
  return pickBestFirstContactFromCandidates(cands);
}

/**
 * First object ball the cue hits by geometry after the shot is underway.
 * This remains as a fallback when shot-start prediction could not lock a target.
 */
function pickGeometricFirstCueContact(white, balls) {
  if (!white.active || white.type !== 'WHITE') return null;
  const wvx = white.vx;
  const wvy = white.vy;
  const wSpeed = Math.hypot(wvx, wvy);
  if (wSpeed < 1e-8) return null;

  function collectCandidates(minApproach) {
    const cands = [];
    for (let i = 0; i < balls.length; i++) {
      const b = balls[i];
      if (!b.active || b.type === 'WHITE') continue;
      const dx = b.x - white.x;
      const dy = b.y - white.y;
      const dist = Math.hypot(dx, dy);
      if (!Number.isFinite(dist) || dist >= BALL_RADIUS * 2) continue;
      const nx = dist > 1e-8 ? dx / dist : 1;
      const ny = dist > 1e-8 ? dy / dist : 0;
      const rvx = white.vx - b.vx;
      const rvy = white.vy - b.vy;
      const velAlongNormal = rvx * nx + rvy * ny;
      if (velAlongNormal <= minApproach) continue;
      const proj = dx * wvx + dy * wvy;
      if (proj <= 0) continue;
      cands.push({ type: b.type, entry: proj / wSpeed, dist });
    }
    return cands;
  }

  function tryTier(minApproach) {
    const cands = collectCandidates(minApproach);
    return pickBestFirstContactFromCandidates(cands);
  }

  return tryTier(MIN_COLLISION_APPROACH) ?? tryTier(FIRST_CONTACT_APPROACH_RELAXED);
}

/** Stable phase for first-contact typing when shot-start snapshot is missing. */
function phaseForFirstContactPick(phaseAtShotStart, redsRemaining, livePhase) {
  if (phaseAtShotStart != null && phaseAtShotStart !== '') return phaseAtShotStart;
  if (redsRemaining > 0) return 'RED';
  return livePhase ?? 'RED';
}

/** Order-independent first object from white–object impulses in one physics substep (min proj along cue among RED pool in RED phase). */
function pickFirstContactTypeFromImpulseHits(hits) {
  if (!hits.length) return null;
  const bestProjByType = new Map();
  for (const h of hits) {
    const p = bestProjByType.get(h.type);
    if (p === undefined || h.proj < p) bestProjByType.set(h.type, h.proj);
  }
  const rows = [...bestProjByType.entries()].map(([type, proj]) => ({ type, proj }));
  const forward = rows.filter((r) => r.proj > 0);
  const pool = forward.length ? forward : rows;
  pool.sort((a, b) => a.proj - b.proj);
  return pool[0].type;
}

function foulPointsForBallType(type) {
  if (!type || type === 'WHITE') return 4;
  const v = BALL_VALUES[type];
  return v != null ? Math.max(4, v) : 4;
}

/** Returns foul points if contact is illegal on a total miss; null if legal. */
function contactFoulPointsOnMiss(phaseAtShotStart, requiredAtShotStart, contact, nominatedColorType) {
  if (phaseAtShotStart === 'GAME_OVER') return null;
  if (phaseAtShotStart === 'RED') {
    if (contact == null) return 4;
    if (contact === 'RED') return null;
    return foulPointsForBallType(contact);
  }
  if (phaseAtShotStart === 'COLOR') {
    if (contact == null) return 4;
    if (contact === 'RED') return Math.max(4, BALL_VALUES.RED);
    if (nominatedColorType && contact !== nominatedColorType) return foulPointsForBallType(contact);
    return null;
  }
  if (phaseAtShotStart === 'COLORS_ONLY') {
    if (contact == null) return 4;
    if (contact === requiredAtShotStart) return null;
    return foulPointsForBallType(contact);
  }
  const req = requiredAtShotStart != null ? requiredAtShotStart : 'RED';
  return contactFoulPointsOnMiss('RED', req, contact, nominatedColorType);
}

/** Ball name for foul copy, e.g. 'RED' -> 'red'. */
function ballLabel(type) {
  if (!type) return 'nothing';
  return String(type).toLowerCase();
}

/** Joins ball names: "blue", or "blue and pink", or "blue, pink and black". */
function ballListLabel(types) {
  const names = (types || []).filter(Boolean).map(ballLabel);
  if (names.length === 0) return 'nothing';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function foulReasonForMiss(phaseAtShotStart, requiredAtShotStart, contact, nominatedColorType) {
  if (phaseAtShotStart === 'RED') {
    if (contact == null) return 'A swing and a miss! You needed to hit a red.';
    if (contact !== 'RED') return `You hit ${ballLabel(contact)} first. Red had the first appointment!`;
    return null;
  }
  if (phaseAtShotStart === 'COLOR') {
    if (contact == null) return 'Nothing connected! You needed to hit a coloured ball.';
    if (contact === 'RED') return 'Red again? This shot needed a colour!';
    if (nominatedColorType && contact !== nominatedColorType) {
      return `You hit ${ballLabel(contact)} first. ${ballLabel(nominatedColorType)} was your target!`;
    }
    return null;
  }
  if (phaseAtShotStart === 'COLORS_ONLY') {
    if (contact == null) return `A swing and a miss! You needed to hit ${ballLabel(requiredAtShotStart)}.`;
    if (contact !== requiredAtShotStart) {
      return `You hit ${ballLabel(contact)} first. ${ballLabel(requiredAtShotStart)} was first in line!`;
    }
    return null;
  }
  return 'Wrong first touch! Hit the required ball first.';
}

function currentTargetLabel(phase, nextRequiredBall) {
  if (phase === 'RED') return 'Pot a RED';
  if (phase === 'COLOR') return 'Choose any COLOR';
  if (phase === 'COLORS_ONLY') return `Pot ${nextRequiredBall || 'YELLOW'}`;
  return '';
}

/** Rules: geometric first contact wins when set; physics is fallback only (e.g. no geometric pick). */
function effectiveFirstObjectBallType(geometricContactEntry, physicsFirstType) {
  return geometricContactEntry?.type ?? physicsFirstType ?? null;
}

function initializeBalls() {
  const initialBalls = [];
  initialBalls.push({ id: 'white', type: 'WHITE', color: COLORS.WHITE, x: CUE_START.x, y: CUE_START.y, vx: 0, vy: 0, active: true, value: 0 });
  const redStartX = SPOTS.PINK.x + BALL_RADIUS * 2.2;
  const redStartY = TABLE_HEIGHT * 0.5;
  const rowGap = BALL_RADIUS * 2;
  const ballGap = BALL_RADIUS * 1.8;
  let redId = 0;
  for (let row = 0; row < 5; row++) {
    for (let col = 0; col <= row; col++) {
      initialBalls.push({ id: `red-${redId++}`, type: 'RED', color: COLORS.RED, x: redStartX + row * rowGap, y: redStartY - (row * ballGap / 2) + col * ballGap, vx: 0, vy: 0, active: true, value: BALL_VALUES.RED });
    }
  }
  initialBalls.push({ id: 'yellow', type: 'YELLOW', color: COLORS.YELLOW, x: SPOTS.YELLOW.x, y: SPOTS.YELLOW.y, vx: 0, vy: 0, active: true, value: BALL_VALUES.YELLOW });
  initialBalls.push({ id: 'green', type: 'GREEN', color: COLORS.GREEN, x: SPOTS.GREEN.x, y: SPOTS.GREEN.y, vx: 0, vy: 0, active: true, value: BALL_VALUES.GREEN });
  initialBalls.push({ id: 'brown', type: 'BROWN', color: COLORS.BROWN, x: SPOTS.BROWN.x, y: SPOTS.BROWN.y, vx: 0, vy: 0, active: true, value: BALL_VALUES.BROWN });
  initialBalls.push({ id: 'blue', type: 'BLUE', color: COLORS.BLUE, x: SPOTS.BLUE.x, y: SPOTS.BLUE.y, vx: 0, vy: 0, active: true, value: BALL_VALUES.BLUE });
  initialBalls.push({ id: 'pink', type: 'PINK', color: COLORS.PINK, x: SPOTS.PINK.x, y: SPOTS.PINK.y, vx: 0, vy: 0, active: true, value: BALL_VALUES.PINK });
  initialBalls.push({ id: 'black', type: 'BLACK', color: COLORS.BLACK, x: SPOTS.BLACK.x, y: SPOTS.BLACK.y, vx: 0, vy: 0, active: true, value: BALL_VALUES.BLACK });
  return initialBalls;
}

function generateRoomCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

function isPresenceFresh(timestamp) {
  if (!timestamp) return false;
  const millis = typeof timestamp === 'number' ? timestamp : (timestamp.seconds ? timestamp.seconds * 1000 : 0);
  if (!millis) return false;
  return Date.now() - millis < PRESENCE_TIMEOUT_MS;
}

// Parse URL query params for deep-linked Snooker games (mirror Ludo)
function parseSnookerConfigFromUrl() {
  if (typeof window === 'undefined') return {};
  try {
    const params = new URLSearchParams(window.location.search || '');
    const rawRoomCode = params.get('roomCode') || params.get('gameId') || params.get('room_code');
    const rawMode = params.get('mode');
    const mode = rawMode === 'computer' || rawMode === 'multiplayer' || rawMode === 'tournament'
      ? (rawMode === 'tournament' ? 'multiplayer' : rawMode)
      : null;
    const roomCode = rawRoomCode ? String(rawRoomCode).trim().toUpperCase().slice(0, ROOM_CODE_MAX_URL_LENGTH) : null;
    const rawColor = params.get('color');
    const seatColor =
      rawColor && ['black', 'white'].includes(String(rawColor).toLowerCase())
        ? String(rawColor).toLowerCase()
        : null;
    const rawRole = params.get('role');
    const isSpectator = String(rawRole || '').toLowerCase() === 'spectator';
    return {
      roomCode: roomCode || null,
      mode,
      rawMode: params.get('mode') || null,
      userId: params.get('userId') || params.get('user_id') || null,
      name: params.get('name') || null,
      color: seatColor,
      tournamentId: params.get('tournamentId') || null,
      isSpectator,
    };
  } catch (e) {
    return {};
  }
}

function computeRemainingShotTime(turnStartAt) {
  if (!turnStartAt) return SHOT_TIME_LIMIT;
  return Math.max(0, SHOT_TIME_LIMIT - Math.floor((Date.now() - turnStartAt) / 1000));
}

function RetryDots({ missed = 0 }) {
  return (
    <div className="retry-dots" aria-label={`${Math.max(0, MAX_MISSED_TURNS - missed)} retries remaining`}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={`retry-dot${i < missed ? ' retry-dot--used' : ''}`}
        />
      ))}
    </div>
  );
}

function SnookerGameOverBallIcon() {
  const uid = useId().replace(/:/g, '');
  const gradId = `snooker-ball-grad-${uid}`;
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" aria-hidden="true">
      <defs>
        <radialGradient id={gradId} cx="35%" cy="28%" r="68%">
          <stop offset="0%" stopColor="#ff7a6e" />
          <stop offset="40%" stopColor="#c0392b" />
          <stop offset="100%" stopColor="#641e18" />
        </radialGradient>
      </defs>
      <circle cx="36" cy="36" r="32" fill={`url(#${gradId})`} />
      <ellipse cx="27" cy="24" rx="11" ry="7" fill="rgba(255,255,255,0.9)" transform="rotate(-22 27 24)" />
    </svg>
  );
}

// ============================================================================
// MAIN APP COMPONENT
// ============================================================================

function SnookerGame({ updateHeader }) {
  const urlConfig = parseSnookerConfigFromUrl();
  const isLiveMode = urlConfig.rawMode === 'live';
  const isTournamentMatch = !!urlConfig.tournamentId;
  const isSpectator = !!urlConfig.isSpectator;
  const initialRejoinCode = urlConfig.roomCode || null;
  // Refs
  const canvasRef = useRef(null);
  const cueCanvasRef = useRef(null);
  /**
   * Transparent canvas sized like the cue overlay (table + CUE_OVERLAY_MARGIN),
   * carrying all pointer handlers. The visible table canvas is only
   * TABLE_WIDTH x TABLE_HEIGHT, so pulling the stick back near a rail/pocket
   * previously required dragging past its edge — off the only element
   * listening for pointer events — which is why aiming near the rail felt
   * unresponsive or dead. This layer gives the same drag room the stick
   * itself is drawn into.
   */
  const inputCanvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastFrameTimeRef = useRef(null);
  const lastSyncTimeRef = useRef(0);
  const lastProcessedShotRef = useRef('');
  const lastRemoteUpdateRef = useRef(0);
  const lastSyncedPlayerRef = useRef(null);
  /**
   * Guards the GAME_OVER sync. Without this, the moment the host's local phase
   * flips to GAME_OVER, syncGameState's own "don't sync a finished game" guard
   * would block the one write that tells the guest the game just ended — the
   * guest never finds out and never sees the win/loss popup.
   */
  const gameOverSyncedRef = useRef(false);
  const firstContactRef = useRef(null);
  const physicsFirstObjectTypeRef = useRef(null);
  /** Ball positions at the end of the previous frame — enables swept pocket capture. */
  const prevBallPositionsRef = useRef(new Map());
  /** Unit direction of cue ball velocity at strike (post CUE_BALL_VELOCITY_SCALE); for first-contact proj along shot line. */
  const cueUnitAtShotStartRef = useRef(null);
  const shotStartWhitePosRef = useRef(null);
  /** Phase / required ball when cue was struck (pots mid-shot change gamePhase — foul rules must use this). */
  const gamePhaseAtShotStartRef = useRef(null);
  const nextRequiredBallAtShotStartRef = useRef(null);
  const shooterPlayerNumberAtShotStartRef = useRef(null);
  const scoresAtShotStartRef = useRef(null);
  const nominatedColorTypeRef = useRef(null);
  const redPotsThisShotRef = useRef(0);
  const pottedThisShotRef = useRef(false);
  const foulThisShotRef = useRef(false);
  const shotResolvedRef = useRef(false);
  const roomIdRef = useRef(null);
  const isHostRef = useRef(false);
  const clientIdRef = useRef(null);
  
  // State
  const [balls, setBalls] = useState(() => initializeBalls());
  const ballsRef = useRef(balls);
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const currentPlayerRef = useRef(1);
  const [scores, setScores] = useState({ player1: 0, player2: 0 });
  const scoresRef = useRef({ player1: 0, player2: 0 });
  const [gamePhase, setGamePhase] = useState('RED');
  const gamePhaseRef = useRef('RED');
  const [redsRemaining, setRedsRemaining] = useState(15);
  const redsRemainingRef = useRef(15);
  const [nextRequiredBall, setNextRequiredBall] = useState('RED');
  const nextRequiredBallRef = useRef('RED');
  /**
   * Aim/drag tracking lives entirely in refs now. The stick is drawn every
   * animation frame from these refs already — routing pointermove through
   * React state as well forced a full component re-render per pointer event,
   * which is what made the drag feel laggy. isAiming/aimStart/aimCurrent as
   * state were write-only (nothing in JSX read them), so this changes no
   * rendered output, just removes the redundant re-renders.
   */
  const isAimingRef = useRef(false);
  const aimStartRef = useRef(null);
  const aimCurrentRef = useRef(null);
  /** Cached canvas rect for the active drag — avoids a layout-forcing getBoundingClientRect() on every pointermove. */
  const pointerRectRef = useRef(null);
  const displayAimRef = useRef(null);
  const aiShotScheduledRef = useRef(false);
  const colorRespotTimeoutRef = useRef(null);
  const pendingSwitchTimeoutRef = useRef(null);
  const foulPopupTimeoutRef = useRef(null);
  const scorePopupTimeoutRef = useRef(null);
  const [ballsMoving, setBallsMoving] = useState(false);
  const ballsMovingRef = useRef(false);
  const [message, setMessage] = useState('Player 1 to play - Pot a RED');
  const messageRef = useRef('Player 1 to play - Pot a RED');
  const [tablePopupEvent, setTablePopupEvent] = useState(null);
  const tablePopupEventRef = useRef(null);
  const [turnContinues, setTurnContinues] = useState(false);
  const turnContinuesRef = useRef(false);
  const [shotInProgress, setShotInProgress] = useState(false);
  const shotInProgressRef = useRef(false);
  const [shotTimeLeft, setShotTimeLeft] = useState(SHOT_TIME_LIMIT);
  const shotTimeLeftRef = useRef(SHOT_TIME_LIMIT);
  const turnStartAtRef = useRef(null);
  const [missedTurns, setMissedTurns] = useState({ player1: 0, player2: 0 });
  const missedTurnsRef = useRef({ player1: 0, player2: 0 });
  const [gameEndReason, setGameEndReason] = useState(null);
  const gameEndReasonRef = useRef(null);
  const [timeoutWinner, setTimeoutWinner] = useState(null);
  const timeoutWinnerRef = useRef(null);

  const [roomId, setRoomId] = useState(null);
  const [roomCode, setRoomCode] = useState('');
  const [roomCodeInput, setRoomCodeInput] = useState('');
  const [roomStatus, setRoomStatus] = useState('offline');
  const [roomError, setRoomError] = useState('');
  const [isHost, setIsHost] = useState(false);
  const [guestId, setGuestId] = useState(null);
  const [hostName, setHostName] = useState('');
  const [guestName, setGuestName] = useState('');
  const [tournamentOpponentName, setTournamentOpponentName] = useState('');
  const [presence, setPresence] = useState({ hostLastSeen: null, guestLastSeen: null });

  const userName = (() => {
    try {
      const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null;
      const data = stored ? JSON.parse(stored) : {};
      return data.name || 'Player 1';
    } catch (e) { return 'Player 1'; }
  })();

  const [autoJoinAttempted, setAutoJoinAttempted] = useState(false);

  const [showLanding, setShowLanding] = useState(!initialRejoinCode);
  const [showYourTurnPopup, setShowYourTurnPopup] = useState(false);
  const [yourTurnPopupText, setYourTurnPopupText] = useState('Your turn');
  /**
   * State mirror of `turnStartAtRef` so the turn popup can react to every new
   * shot grant — including when the same player keeps the table after a pot,
   * where `isLocalTurn` alone never changes.
   */
  const [turnStartAtState, setTurnStartAtState] = useState(null);
  const lastTurnPopupPlayerRef = useRef(null);
  /** First synced snapshot tells us whether we loaded straight into a finished match (e.g. after a refresh). */
  const firstRemoteStateSeenRef = useRef(false);
  const loadedIntoFinishedMatchRef = useRef(false);
  /** Guards the one-time backend lookup that restores the result popup after a refresh. */
  const matchResultRestoredRef = useRef(false);
  const [foulPopupText, setFoulPopupText] = useState(null);
  const [foulPopupReason, setFoulPopupReason] = useState(null);
  const [scorePopupText, setScorePopupText] = useState(null);
  const lastRenderedPopupEventIdRef = useRef(null);
  const [gameOverModal, setGameOverModal] = useState(null);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const autoShowHowToPlayRef = useRef(false);
  const [leaveConfirmModal, setLeaveConfirmModal] = useState(null);
  const [gameMode, setGameMode] = useState(
    urlConfig.mode === 'computer' || urlConfig.mode === 'multiplayer' ? urlConfig.mode : 'multiplayer'
  );

  const opponentDisplayName =
    tournamentOpponentName || (isTournamentMatch ? 'Waiting for opponent...' : 'Player 2');

  const player1Name =
    isSpectator
      ? (hostName || 'Player 1')
      : gameMode === 'multiplayer'
        ? isHost
          ? hostName || userName
          : hostName || opponentDisplayName
        : userName;

  const player2Name =
    gameMode === 'computer'
      ? 'Computer'
      : isSpectator
        ? (guestName || 'Player 2')
        : gameMode === 'multiplayer'
          ? isHost
            ? guestName || opponentDisplayName
            : guestName || userName
          : guestName || 'Player 2';
  const [liveThemeFlags, setLiveThemeFlags] = useState({ computer: true, multiplayer: true });
  const [gameStarted, setGameStarted] = useState(false);
  const rejoinAttemptedRef = useRef(false);
  const checkingExistingRef = useRef(false);
  const gameOverNotifiedRef = useRef(false);
  const tournamentAutoStartAttemptedRef = useRef(false);
  const shotExpiredHandledRef = useRef(false);
  const presentMarkedRef = useRef({});
  const lobbyTimerIntervalRef = useRef(null);
  const lobbyWinNotifiedRef = useRef(false);
  const [clientIdReady, setClientIdReady] = useState(false);
  const [lobbyTimerSeconds, setLobbyTimerSeconds] = useState(LOBBY_WAIT_SECONDS);
  const [lobbyTimeout, setLobbyTimeout] = useState(false);
  const [lobbyWinnerSeat, setLobbyWinnerSeat] = useState(null);
  const [hostSeatColor, setHostSeatColor] = useState(null);
  const [guestSeatColor, setGuestSeatColor] = useState(null);
  
  // Derived state (Must be defined after state hooks)
  const isAuthoritative = !isSpectator && (!roomId || isHost);
  const localPlayerNumber = gameMode === 'computer' ? 1 : (roomId ? (isHost ? 1 : 2) : currentPlayer);
  const isLocalTurn =
    !isSpectator &&
    (gameMode === 'computer' ? currentPlayer === 1 : currentPlayer === localPlayerNumber) &&
    (gameMode === 'computer' || !roomId || (isTournamentMatch ? gameStarted : true));

  useEffect(() => {
    if (!isLocalTurn || gamePhase === 'GAME_OVER') {
      setShowYourTurnPopup(false);
      // Turn handed over: next grant is a fresh turn, not a retained one.
      lastTurnPopupPlayerRef.current = null;
      return;
    }
    // Same player granted another shot without the turn changing hands => retained.
    const isRetainedTurn = lastTurnPopupPlayerRef.current === localPlayerNumber;
    lastTurnPopupPlayerRef.current = localPlayerNumber;
    setYourTurnPopupText(isRetainedTurn ? 'Your turn again' : 'Your turn');
    setShowYourTurnPopup(true);
    const t = setTimeout(() => setShowYourTurnPopup(false), 1800);
    return () => clearTimeout(t);
  }, [isLocalTurn, turnStartAtState, localPlayerNumber, gamePhase]);

  /**
   * Show How to Play once, the first time the actual table becomes visible —
   * i.e. past the landing screen and (for tournament matches) past the
   * waiting-for-opponent lobby. Guarded by a ref so it only auto-opens once
   * per session; the player can still reopen it anytime via the "i" button.
   */
  useEffect(() => {
    if (autoShowHowToPlayRef.current) return;
    const isInTournamentLobby =
      gameMode === 'multiplayer' && roomId && !gameStarted && isTournamentMatch;
    const isTableVisible = !showLanding && !isInTournamentLobby;
    if (isTableVisible) {
      autoShowHowToPlayRef.current = true;
      setShowHowToPlay(true);
    }
  }, [showLanding, gameMode, roomId, gameStarted, isTournamentMatch]);

  /**
   * Foul banner text, written from the viewer's point of view.
   * The popup event is synced, so the wording must be built locally on each
   * client — the player who fouled sees "You missed!", everyone else sees the
   * offender by name.
   */
  const buildFoulPopupTitle = useCallback((foulingPlayerNum, points) => {
    if (!foulingPlayerNum) return null;
    const offenderNum = Number(foulingPlayerNum);
    const beneficiaryNum = offenderNum === 1 ? 2 : 1;
    const offenderName = offenderNum === 1 ? player1Name : player2Name;
    const beneficiaryName = beneficiaryNum === 1 ? player1Name : player2Name;
    const viewerIsOffender = !isSpectator && localPlayerNumber === offenderNum;
    const viewerIsBeneficiary = !isSpectator && localPlayerNumber === beneficiaryNum;
    const who = viewerIsOffender ? 'You' : offenderName;
    const to = viewerIsBeneficiary ? 'you' : beneficiaryName;
    return `${who} missed! ${points} points to ${to}`;
  }, [player1Name, player2Name, localPlayerNumber, isSpectator]);

  /** Render synced table popups so both host and guest see the same foul / score banners. */
  useEffect(() => {
    const event = tablePopupEvent;
    if (!event?.id) return undefined;
    if (lastRenderedPopupEventIdRef.current === event.id) return undefined;
    lastRenderedPopupEventIdRef.current = event.id;

    if (foulPopupTimeoutRef.current) clearTimeout(foulPopupTimeoutRef.current);
    if (scorePopupTimeoutRef.current) clearTimeout(scorePopupTimeoutRef.current);

    // Prefer locally-built, viewer-relative wording; fall back to the synced text.
    const localFoulTitle =
      buildFoulPopupTitle(event.foulingPlayer, event.foulPoints) || event.foulText || null;

    setFoulPopupText(localFoulTitle);
    setFoulPopupReason(event.foulReason || null);
    setScorePopupText(event.scoreText || null);

    if (localFoulTitle) {
      foulPopupTimeoutRef.current = setTimeout(() => {
        foulPopupTimeoutRef.current = null;
        setFoulPopupText(null);
        setFoulPopupReason(null);
      }, TABLE_TOAST_MS);
    } else {
      setFoulPopupText(null);
      setFoulPopupReason(null);
    }

    if (event.scoreText) {
      scorePopupTimeoutRef.current = setTimeout(() => {
        scorePopupTimeoutRef.current = null;
        setScorePopupText(null);
      }, TABLE_TOAST_MS);
    } else {
      setScorePopupText(null);
    }
    return undefined;
  }, [tablePopupEvent, buildFoulPopupTitle]);

  const formatScorePopup = useCallback((playerLabel, points) => `${playerLabel}: +${points} Points`, []);

  useEffect(() => () => {
    if (colorRespotTimeoutRef.current) clearTimeout(colorRespotTimeoutRef.current);
    if (pendingSwitchTimeoutRef.current) clearTimeout(pendingSwitchTimeoutRef.current);
    if (foulPopupTimeoutRef.current) clearTimeout(foulPopupTimeoutRef.current);
    if (scorePopupTimeoutRef.current) clearTimeout(scorePopupTimeoutRef.current);
  }, []);

  // Callbacks
  const updateGameState = useCallback((updates) => {
    if (updates.balls !== undefined) { setBalls(updates.balls); ballsRef.current = updates.balls; }
    if (updates.currentPlayer !== undefined) { setCurrentPlayer(updates.currentPlayer); currentPlayerRef.current = updates.currentPlayer; }
    if (updates.scores !== undefined) { setScores(updates.scores); scoresRef.current = updates.scores; }
    if (updates.gamePhase !== undefined) { setGamePhase(updates.gamePhase); gamePhaseRef.current = updates.gamePhase; }
    if (updates.redsRemaining !== undefined) { setRedsRemaining(updates.redsRemaining); redsRemainingRef.current = updates.redsRemaining; }
    if (updates.nextRequiredBall !== undefined) { setNextRequiredBall(updates.nextRequiredBall); nextRequiredBallRef.current = updates.nextRequiredBall; }
    if (updates.ballsMoving !== undefined) {
      setBallsMoving(updates.ballsMoving); ballsMovingRef.current = updates.ballsMoving;
    }
    if (updates.message !== undefined) { setMessage(updates.message); messageRef.current = updates.message; }
    if (updates.tablePopupEvent !== undefined) { setTablePopupEvent(updates.tablePopupEvent); tablePopupEventRef.current = updates.tablePopupEvent; }
    if (updates.turnContinues !== undefined) { setTurnContinues(updates.turnContinues); turnContinuesRef.current = updates.turnContinues; }
    if (updates.shotInProgress !== undefined) {
      setShotInProgress(updates.shotInProgress); shotInProgressRef.current = updates.shotInProgress;
    }
    if (updates.shotTimeLeft !== undefined) { setShotTimeLeft(updates.shotTimeLeft); shotTimeLeftRef.current = updates.shotTimeLeft; }
    if (updates.turnStartAt !== undefined) { turnStartAtRef.current = updates.turnStartAt; setTurnStartAtState(updates.turnStartAt); }
    if (updates.missedTurns !== undefined) { setMissedTurns(updates.missedTurns); missedTurnsRef.current = updates.missedTurns; }
    if (updates.gameEndReason !== undefined) { setGameEndReason(updates.gameEndReason); gameEndReasonRef.current = updates.gameEndReason; }
    if (updates.timeoutWinner !== undefined) { setTimeoutWinner(updates.timeoutWinner); timeoutWinnerRef.current = updates.timeoutWinner; }
  }, []);

  const publishTablePopupEvent = useCallback((payload) => {
    const hasPopup = payload && (payload.foulText || payload.scoreText);
    if (!hasPopup) return;
    updateGameState({
      tablePopupEvent: {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        foulText: payload.foulText || null,
        foulReason: payload.foulReason || null,
        // Carried so each client can phrase the banner from its own viewpoint.
        foulingPlayer: payload.foulingPlayer || null,
        foulPoints: payload.foulPoints ?? null,
        scoreText: payload.scoreText || null
      }
    });
  }, [updateGameState]);

  const queueScorePopup = useCallback((text) => {
    publishTablePopupEvent({ scoreText: text });
  }, [publishTablePopupEvent]);

  const updateMessage = useCallback(() => {
    if (!isAuthoritative) return;
    const playerNum = currentPlayerRef.current;
    const playerLabel = playerNum === 1 ? player1Name : player2Name;
    const ballMsg = currentTargetLabel(gamePhaseRef.current, nextRequiredBallRef.current);
    updateGameState({ message: `${playerLabel} to play - ${ballMsg}` });
  }, [isAuthoritative, updateGameState, player1Name, player2Name]);

  useEffect(() => {
    if (!isAuthoritative) return;
    if (gamePhaseRef.current === 'GAME_OVER') return;
    const activeReds = countActiveReds(ballsRef.current);
    const activeObjects = countActiveObjectBalls(ballsRef.current);
    if (activeObjects === 0) return;

    if (activeReds === 0) {
      const requiredColor = nextColorRequiredOnTable(ballsRef.current, nextRequiredBallRef.current);
      const needsRepair =
        redsRemainingRef.current !== 0 ||
        gamePhaseRef.current !== 'COLORS_ONLY' ||
        nextRequiredBallRef.current !== requiredColor;
      if (needsRepair) {
        updateGameState({
          redsRemaining: 0,
          gamePhase: 'COLORS_ONLY',
          nextRequiredBall: requiredColor
        });
        updateMessage();
      }
      return;
    }

    if (redsRemainingRef.current !== activeReds) {
      updateGameState({ redsRemaining: activeReds });
    }
  }, [balls, isAuthoritative, updateGameState, updateMessage]);

  const resetShotTimer = useCallback((value = SHOT_TIME_LIMIT) => {
    const now = Date.now();
    turnStartAtRef.current = now;
    shotExpiredHandledRef.current = false;
    updateGameState({ shotTimeLeft: value, turnStartAt: now });
    if (roomIdRef.current && isHostRef.current) {
      lastSyncTimeRef.current = 0;
    }
  }, [updateGameState]);

  const resetPerShotRuleRefs = useCallback(() => {
    firstContactRef.current = null;
    physicsFirstObjectTypeRef.current = null;
    cueUnitAtShotStartRef.current = null;
    shotStartWhitePosRef.current = null;
    gamePhaseAtShotStartRef.current = null;
    nextRequiredBallAtShotStartRef.current = null;
    shooterPlayerNumberAtShotStartRef.current = null;
    scoresAtShotStartRef.current = null;
    nominatedColorTypeRef.current = null;
    redPotsThisShotRef.current = 0;
    pottedThisShotRef.current = false;
    foulThisShotRef.current = false;
  }, []);

  const resetMatchToInitialState = useCallback((firstPlayerLabel = 'Player 1') => {
    const initialBalls = initializeBalls();
    prevBallPositionsRef.current = new Map();
    gameOverSyncedRef.current = false;
    setBalls(initialBalls);
    ballsRef.current = initialBalls;
    setScores({ player1: 0, player2: 0 });
    scoresRef.current = { player1: 0, player2: 0 };
    setCurrentPlayer(1);
    currentPlayerRef.current = 1;
    setGamePhase('RED');
    gamePhaseRef.current = 'RED';
    setRedsRemaining(15);
    redsRemainingRef.current = 15;
    setNextRequiredBall('RED');
    nextRequiredBallRef.current = 'RED';
    const firstMsg = `${firstPlayerLabel} to play - Pot a RED`;
    setMessage(firstMsg);
    messageRef.current = firstMsg;
    setTablePopupEvent(null);
    tablePopupEventRef.current = null;
    lastRenderedPopupEventIdRef.current = null;
    setTurnContinues(false);
    turnContinuesRef.current = false;
    setBallsMoving(false);
    ballsMovingRef.current = false;
    setShotInProgress(false);
    shotInProgressRef.current = false;
    setShotTimeLeft(SHOT_TIME_LIMIT);
    shotTimeLeftRef.current = SHOT_TIME_LIMIT;
    const now = Date.now();
    turnStartAtRef.current = now;
    shotExpiredHandledRef.current = false;
    const initialMissedTurns = { player1: 0, player2: 0 };
    setMissedTurns(initialMissedTurns);
    missedTurnsRef.current = initialMissedTurns;
    setGameEndReason(null);
    gameEndReasonRef.current = null;
    setTimeoutWinner(null);
    timeoutWinnerRef.current = null;
    setFoulPopupText(null);
    setFoulPopupReason(null);
    setScorePopupText(null);
    displayAimRef.current = null;
    aimCurrentRef.current = null;
    shotResolvedRef.current = false;
    resetPerShotRuleRefs();
  }, [resetPerShotRuleRefs]);

  const lockFirstContactFromShotStart = useCallback((whiteStart, cueUnit, ballsArray, phaseAtShotStart) => {
    if (!whiteStart || !cueUnit) return;
    const pick = predictFirstCueContactFromShotLine(whiteStart, cueUnit, ballsArray);
    if (!pick) return;
    if (phaseAtShotStart === 'COLOR' && pick.type !== 'RED') nominatedColorTypeRef.current = pick.type;
  }, []);

  const checkGameEnd = useCallback(() => {
    if (!isAuthoritative) return;
    const activeBalls = ballsRef.current.filter(b => b.active && b.type !== 'WHITE');
    if (activeBalls.length === 0) {
      const winner = scoresRef.current.player1 > scoresRef.current.player2 ? 1 : 2;
      const winnerLabel = winner === 1 ? player1Name : player2Name;
      updateGameState({
        gamePhase: 'GAME_OVER',
        gameEndReason: 'score',
        timeoutWinner: null,
        message: `GAME OVER! ${winnerLabel} wins ${scoresRef.current.player1} - ${scoresRef.current.player2}`,
      });
    }
  }, [isAuthoritative, updateGameState, player1Name, player2Name]);

  const normalizeIncomingTurnState = useCallback(() => {
    if (gamePhaseRef.current === 'GAME_OVER') return {};
    if (redsRemainingRef.current > 0) {
      return { gamePhase: 'RED', nextRequiredBall: 'RED' };
    }
    if (gamePhaseRef.current !== 'COLORS_ONLY') {
      return { gamePhase: 'COLORS_ONLY', nextRequiredBall: 'YELLOW' };
    }
    return {};
  }, []);

  const switchPlayer = useCallback(() => {
    if (!isAuthoritative) return;
    resetPerShotRuleRefs();
    const nextPlayer = currentPlayerRef.current === 1 ? 2 : 1;
    shotResolvedRef.current = false;
    updateGameState({ currentPlayer: nextPlayer, turnContinues: false, ...normalizeIncomingTurnState() });
    resetShotTimer();
    updateMessage();
  }, [isAuthoritative, updateGameState, updateMessage, resetShotTimer, resetPerShotRuleRefs, normalizeIncomingTurnState]);

  const scheduleSwitchPlayer = useCallback((delayMs = 0) => {
    if (!isAuthoritative) return;
    if (pendingSwitchTimeoutRef.current) {
      clearTimeout(pendingSwitchTimeoutRef.current);
      pendingSwitchTimeoutRef.current = null;
    }
    const runSwitch = () => {
      pendingSwitchTimeoutRef.current = null;
      if (!isAuthoritative) return;
      console.log('[Snooker] Switching turn');
      switchPlayer();
    };
    if (delayMs <= 0) {
      runSwitch();
      return;
    }
    pendingSwitchTimeoutRef.current = setTimeout(runSwitch, delayMs);
  }, [isAuthoritative, switchPlayer]);

  const endTurn = useCallback(() => {
    if (!isAuthoritative) return;
    resetPerShotRuleRefs();
    updateGameState({ turnContinues: false });
    scheduleSwitchPlayer(0);
  }, [isAuthoritative, updateGameState, scheduleSwitchPlayer, resetPerShotRuleRefs]);

  const handleShotTimerExpired = useCallback(() => {
    if (!isAuthoritative) return;
    if (gamePhaseRef.current === 'GAME_OVER') return;

    if (!isTournamentMatch || gameMode !== 'multiplayer') {
      endTurn();
      return;
    }

    const timedOutPlayer = currentPlayerRef.current;
    const key = timedOutPlayer === 1 ? 'player1' : 'player2';
    const currentMissed = missedTurnsRef.current[key] ?? 0;
    const newMissed = Math.min(MAX_MISSED_TURNS, currentMissed + 1);
    const newMissedTurns = { ...missedTurnsRef.current, [key]: newMissed };

    if (newMissed >= MAX_MISSED_TURNS) {
      const winner = timedOutPlayer === 1 ? 2 : 1;
      const timedOutLabel = timedOutPlayer === 1 ? player1Name : player2Name;
      const winnerLabel = winner === 1 ? player1Name : player2Name;
      updateGameState({
        missedTurns: newMissedTurns,
        gamePhase: 'GAME_OVER',
        gameEndReason: 'abandonment',
        timeoutWinner: winner,
        turnContinues: false,
        message: `GAME OVER! ${winnerLabel} wins — ${timedOutLabel} abandoned (3 missed shots)`,
      });
      return;
    }

    updateGameState({ missedTurns: newMissedTurns, turnContinues: false });
    publishTablePopupEvent({ scoreText: `Shot timer expired — retry used (${newMissed}/${MAX_MISSED_TURNS})` });
    resetPerShotRuleRefs();
    if (roomIdRef.current && isHostRef.current) {
      lastSyncTimeRef.current = 0;
    }
    scheduleSwitchPlayer(0);
  }, [
    isAuthoritative,
    isTournamentMatch,
    gameMode,
    endTurn,
    updateGameState,
    publishTablePopupEvent,
    resetPerShotRuleRefs,
    scheduleSwitchPlayer,
    player1Name,
    player2Name,
  ]);

  const handleFoul = useCallback((points, options = {}) => {
    if (!isAuthoritative) return;
    if (foulThisShotRef.current) return;
    const shooter = options.foulingPlayer ?? shooterPlayerNumberAtShotStartRef.current ?? currentPlayerRef.current;
    const opponent = shooter === 1 ? 'player2' : 'player1';
    foulThisShotRef.current = true;
    const newScores = { ...scoresRef.current, [opponent]: scoresRef.current[opponent] + points };
    let newBalls = ballsRef.current.map(ball => {
      if (ball.type === 'WHITE' && !ball.active) {
        return { ...ball, active: true, x: CUE_START.x, y: CUE_START.y, vx: 0, vy: 0 };
      }
      return ball;
    });
    const respotColorTypes = [...new Set((options.respotColorTypes || []).filter(Boolean))];
    respotColorTypes.forEach(colorType => {
      const spot = SPOTS[colorType];
      if (!spot) return;
      const idx = newBalls.findIndex(ball => ball.type === colorType && !ball.active);
      if (idx === -1) return;
      const pos = findRespotPosition(spot.x, spot.y, newBalls);
      newBalls = newBalls.map((ball, ballIdx) => (
        ballIdx === idx
          ? { ...ball, active: true, x: pos.x, y: pos.y, vx: 0, vy: 0 }
          : ball
      ));
    });
    const opponentLabel = shooter === 1 ? player2Name : player1Name;
    const foulReason = options.reason || 'That shot broke the rules! Your opponent gets the points.';
    console.log('[Snooker] Foul committed:', points, 'points | reason:', foulReason, '| fouling player:', shooter);
    publishTablePopupEvent({
      foulText: `FOUL! ${points} points to ${opponentLabel}`,
      foulReason,
      foulingPlayer: shooter,
      foulPoints: points,
      scoreText: formatScorePopup(opponentLabel, points)
    });
    updateGameState({ scores: newScores, balls: newBalls, message: `FOUL! ${points} points to ${opponentLabel}`, turnContinues: false });
  }, [isAuthoritative, updateGameState, player1Name, player2Name, publishTablePopupEvent, formatScorePopup]);

  const revertPhaseAndStrikerScoreAfterPotFoul = useCallback((phaseForRules, strikerScoreKey) => {
    const startPhase = gamePhaseAtShotStartRef.current ?? phaseForRules;
    const startReq = nextRequiredBallAtShotStartRef.current ?? nextRequiredBallRef.current;
    const snap = scoresAtShotStartRef.current;
    const phaseMismatch = gamePhaseRef.current !== startPhase || nextRequiredBallRef.current !== startReq;
    if (snap) {
      const newScores = { ...scoresRef.current, [strikerScoreKey]: snap[strikerScoreKey] };
      if (phaseMismatch) updateGameState({ scores: newScores, gamePhase: startPhase, nextRequiredBall: startReq });
      else updateGameState({ scores: newScores });
    } else if (phaseMismatch) {
      updateGameState({ gamePhase: startPhase, nextRequiredBall: startReq });
    }
  }, [updateGameState]);

  const processPottedBalls = useCallback((pottedBalls) => {
    if (!isAuthoritative) return;
    if (foulThisShotRef.current) return;
    pottedThisShotRef.current = true;
    const striker = shooterPlayerNumberAtShotStartRef.current ?? currentPlayerRef.current;
    const strikerScoreKey = striker === 1 ? 'player1' : 'player2';
    const strikerLabel = striker === 1 ? player1Name : player2Name;
    const phaseForRules = gamePhaseAtShotStartRef.current ?? gamePhaseRef.current;
    let cueBallPotted = false;
    let redPotted = false;
    let redPotCount = 0;
    let colorPotted = null;
    const pottedColors = [];
    pottedBalls.forEach(ball => {
      if (ball.type === 'WHITE') cueBallPotted = true;
      else if (ball.type === 'RED') {
        redPotted = true;
        redPotCount += 1;
      } else {
        colorPotted = ball;
        pottedColors.push(ball);
      }
    });
    if (cueBallPotted) {
      handleFoul(4, { foulingPlayer: striker, reason: 'Oops! The white ball took a pocket break. That\u2019s a foul!', respotColorTypes: pottedColors.map(ball => ball.type) });
      revertPhaseAndStrikerScoreAfterPotFoul(phaseForRules, strikerScoreKey);
      return;
    }
    const requiredForColorsOnly = nextRequiredBallAtShotStartRef.current ?? nextRequiredBallRef.current;
    const effectiveFirst = effectiveFirstObjectBallType(firstContactRef.current, physicsFirstObjectTypeRef.current);
    console.log('[Snooker] Potted balls:', pottedBalls.map(b => b.type).join(','), '| phaseForRules:', phaseForRules, '| gamePhaseRef:', gamePhaseRef.current);
    if (phaseForRules === 'RED') {
      if (pottedColors.length > 0) {
        const highestColorValue = Math.max(...pottedColors.map(ball => ball.value || 0));
        handleFoul(Math.max(4, highestColorValue), { foulingPlayer: striker, reason: `You pocketed ${ballListLabel(pottedColors.map(ball => ball.type))}. Red was the target!`, respotColorTypes: pottedColors.map(ball => ball.type) });
        revertPhaseAndStrikerScoreAfterPotFoul(phaseForRules, strikerScoreKey);
        return;
      } else if (redPotted) {
        const newScores = { ...scoresRef.current, [strikerScoreKey]: scoresRef.current[strikerScoreKey] + (redPotCount * BALL_VALUES.RED) };
        const newReds = Math.max(0, redsRemainingRef.current - redPotCount);
        let nextPhase = 'COLOR'; let nextBall = 'COLOR (choose any)';
        if (newReds === 0) { nextPhase = 'COLORS_ONLY'; nextBall = 'YELLOW'; }
        updateGameState({ scores: newScores, redsRemaining: newReds, gamePhase: nextPhase, nextRequiredBall: nextBall, turnContinues: true });
        queueScorePopup(formatScorePopup(strikerLabel, redPotCount * BALL_VALUES.RED));
        redPotsThisShotRef.current += redPotCount;
      } else if (effectiveFirst && effectiveFirst !== 'RED') {
        handleFoul(foulPointsForBallType(effectiveFirst), { foulingPlayer: striker, reason: `You hit ${ballLabel(effectiveFirst)} first. Red had the first appointment!` });
        revertPhaseAndStrikerScoreAfterPotFoul(phaseForRules, strikerScoreKey);
        return;
      }
    } else if (phaseForRules === 'COLOR') {
      if (redPotted || pottedColors.length > 1) {
        const maxIllegal = Math.max(
          4,
          redPotted ? BALL_VALUES.RED : 0,
          ...pottedColors.map(ball => ball.value || 0)
        );
        const illegalPotReason = redPotted
          ? (pottedColors.length > 0
            ? `You pocketed red and ${ballListLabel(pottedColors.map(ball => ball.type))}. This shot called for just one colour!`
            : 'Another red? It was a colour\u2019s turn!')
          : `You pocketed ${ballListLabel(pottedColors.map(ball => ball.type))}. One colour at a time, please!`;
        handleFoul(maxIllegal, { foulingPlayer: striker, reason: illegalPotReason, respotColorTypes: pottedColors.map(ball => ball.type) });
        revertPhaseAndStrikerScoreAfterPotFoul(phaseForRules, strikerScoreKey);
        return;
      } else if (colorPotted) {
        const nrAtShotStart = nextRequiredBallAtShotStartRef.current;
        if (nrAtShotStart !== 'COLOR (choose any)') {
          // Defensive fallback: COLOR phase should always carry
          // nextRequiredBall === 'COLOR (choose any)'. If it ever doesn't,
          // this isn't a real "wrong nominated colour" situation (nothing was
          // actually nominated to compare against), so this uses the generic
          // illegal-shot wording rather than claiming a target that wasn't set.
          handleFoul(Math.max(4, colorPotted.value), { foulingPlayer: striker, reason: 'That shot broke the rules! Your opponent gets the points.', respotColorTypes: [colorPotted.type] });
          revertPhaseAndStrikerScoreAfterPotFoul(phaseForRules, strikerScoreKey);
          return;
        }
        if (effectiveFirst === 'RED' || effectiveFirst == null) {
          handleFoul(Math.max(4, colorPotted.value), {
            foulingPlayer: striker,
            reason: effectiveFirst === 'RED'
              ? `You pocketed ${ballLabel(colorPotted.type)}, but hit red first. Colour needed the first touch!`
              : `You pocketed ${ballLabel(colorPotted.type)}, but didn\u2019t hit it first. First touch matters!`,
            respotColorTypes: [colorPotted.type]
          });
          revertPhaseAndStrikerScoreAfterPotFoul(phaseForRules, strikerScoreKey);
          return;
        }
        const nominated = nominatedColorTypeRef.current || effectiveFirst;
        if (colorPotted.type !== nominated) {
          handleFoul(Math.max(4, colorPotted.value, BALL_VALUES[effectiveFirst] || 0), { foulingPlayer: striker, reason: `You hit ${ballLabel(nominated)} first but pocketed ${ballLabel(colorPotted.type)}. Same colour, start to finish!`, respotColorTypes: [colorPotted.type] });
          revertPhaseAndStrikerScoreAfterPotFoul(phaseForRules, strikerScoreKey);
          return;
        }
        const newScores = { ...scoresRef.current, [strikerScoreKey]: scoresRef.current[strikerScoreKey] + colorPotted.value };
        const colorType = colorPotted.type;
        updateGameState({ scores: newScores, gamePhase: 'RED', nextRequiredBall: 'RED', turnContinues: true });
        queueScorePopup(formatScorePopup(strikerLabel, colorPotted.value));
        if (colorRespotTimeoutRef.current) clearTimeout(colorRespotTimeoutRef.current);
        colorRespotTimeoutRef.current = setTimeout(() => {
          colorRespotTimeoutRef.current = null;
          const spot = SPOTS[colorType];
          if (!spot) return;
          const merged = ballsRef.current.map(ball => {
            if (ball.type === colorType && !ball.active) {
              const pos = findRespotPosition(spot.x, spot.y, ballsRef.current);
              return { ...ball, active: true, x: pos.x, y: pos.y, vx: 0, vy: 0 };
            }
            return ball;
          });
          updateGameState({ balls: merged });
        }, COLOR_RESPOT_DELAY_MS);
      }
    } else if (phaseForRules === 'COLORS_ONLY') {
      const correctOrder = ['YELLOW', 'GREEN', 'BROWN', 'BLUE', 'PINK', 'BLACK'];
      if (redPotted || pottedColors.length > 1) {
        const maxIllegal = Math.max(
          4,
          redPotted ? BALL_VALUES.RED : 0,
          ...pottedColors.map(ball => ball.value || 0)
        );
        const illegalReason = redPotted
          ? `You pocketed red, but ${ballLabel(requiredForColorsOnly)} was up next. Stick to the queue!`
          : `You pocketed ${ballListLabel(pottedColors.map(ball => ball.type))}. Only ${ballLabel(requiredForColorsOnly)} was invited!`;
        handleFoul(maxIllegal, { foulingPlayer: striker, reason: illegalReason, respotColorTypes: pottedColors.map(ball => ball.type) });
        revertPhaseAndStrikerScoreAfterPotFoul(phaseForRules, strikerScoreKey);
        return;
      } else if (colorPotted && colorPotted.type === requiredForColorsOnly) {
        if (effectiveFirst !== requiredForColorsOnly) {
          handleFoul(Math.max(4, colorPotted.value, foulPointsForBallType(effectiveFirst)), { foulingPlayer: striker, reason: `You hit ${ballLabel(effectiveFirst)} first. ${ballLabel(requiredForColorsOnly)} was first in line!`, respotColorTypes: [colorPotted.type] });
          revertPhaseAndStrikerScoreAfterPotFoul(phaseForRules, strikerScoreKey);
          return;
        }
        const newScores = { ...scoresRef.current, [strikerScoreKey]: scoresRef.current[strikerScoreKey] + colorPotted.value };
        const currentIndex = correctOrder.indexOf(requiredForColorsOnly);
        queueScorePopup(formatScorePopup(strikerLabel, colorPotted.value));
        if (currentIndex < correctOrder.length - 1) {
          updateGameState({ scores: newScores, nextRequiredBall: correctOrder[currentIndex + 1], turnContinues: true });
        } else {
          updateGameState({ scores: newScores });
          checkGameEnd();
        }
      } else {
        if (colorPotted) {
          handleFoul(Math.max(4, colorPotted.value), { foulingPlayer: striker, reason: `You pocketed ${ballLabel(colorPotted.type)}, but ${ballLabel(requiredForColorsOnly)} was next. No skipping the queue!`, respotColorTypes: [colorPotted.type] });
          revertPhaseAndStrikerScoreAfterPotFoul(phaseForRules, strikerScoreKey);
          return;
        }
      }
    }
    if (gamePhaseRef.current !== 'GAME_OVER' && countActiveObjectBalls(ballsRef.current) === 0) {
      checkGameEnd();
    }
    if (!foulThisShotRef.current && gamePhaseRef.current !== 'GAME_OVER') {
      updateMessage();
    }
  }, [isAuthoritative, updateGameState, handleFoul, revertPhaseAndStrikerScoreAfterPotFoul, updateMessage, checkGameEnd, queueScorePopup, formatScorePopup, player1Name, player2Name]);

  const checkPockets = useCallback(() => {
    if (!isAuthoritative) return;
    const prevBalls = ballsRef.current;
    const priorPositions = prevBallPositionsRef.current;
    let pottedBalls = [];
    const newBalls = prevBalls.map(ball => {
      if (!ball.active) return ball;
      const prior = priorPositions.get(ball.id);
      for (const pocket of POCKETS) {
        const dx = ball.x - pocket.x;
        const dy = ball.y - pocket.y;
        const distance = Math.hypot(dx, dy);
        let captured = distance < POCKET_CAPTURE_RADIUS;

        // Swept fallback: the end-of-frame sample can straddle the pocket when the
        // ball travels further in one frame than the capture radius.
        if (!captured && prior) {
          const segLen = Math.hypot(ball.x - prior.x, ball.y - prior.y);
          if (segLen > 0 && segLen <= MAX_SWEPT_SEGMENT) {
            const sweptDist = pointToSegmentDistance(
              pocket.x, pocket.y, prior.x, prior.y, ball.x, ball.y
            );
            if (sweptDist < POCKET_CAPTURE_RADIUS) {
              captured = true;
              console.log(
                '[Snooker] Swept pocket capture (recovered a pot the per-frame check would have missed):',
                ball.type, ball.id,
                '| frame travel:', segLen.toFixed(1) + 'px',
                '| end-point dist:', distance.toFixed(1) + 'px',
                '| swept dist:', sweptDist.toFixed(1) + 'px',
                '| capture radius:', POCKET_CAPTURE_RADIUS
              );
            }
          } else if (segLen > MAX_SWEPT_SEGMENT) {
            console.log(
              '[Snooker] Skipping swept capture for', ball.type, ball.id,
              '— jump of', segLen.toFixed(1) + 'px looks like a respot, not travel'
            );
          }
        }

        if (captured) {
          pottedBalls.push({ ...ball });
          return { ...ball, active: false, vx: 0, vy: 0 };
        }
      }
      return ball;
    });

    // Remember this frame's positions so the next frame can sweep from here.
    const nextPositions = new Map();
    newBalls.forEach(ball => {
      if (ball.active) nextPositions.set(ball.id, { x: ball.x, y: ball.y });
    });
    prevBallPositionsRef.current = nextPositions;

    if (pottedBalls.length > 0) {
      updateGameState({ balls: newBalls });
      processPottedBalls(pottedBalls);
    }
  }, [isAuthoritative, updateGameState, processPottedBalls]);

  const updatePhysics = useCallback((deltaMs = BASE_FRAME_MS) => {
    if (!isAuthoritative) return;
    const safeDeltaMs = Math.max(deltaMs, 1);
    const currentBalls = ballsRef.current;
    let newBalls = currentBalls.map(ball => ({ ...ball }));

    for (let sub = 0; sub < PHYSICS_SUBSTEPS; sub++) {
      const frameScale = (safeDeltaMs / PHYSICS_SUBSTEPS) / BASE_FRAME_MS;
      const friction = Math.pow(FRICTION, frameScale);
      for (let i = 0; i < newBalls.length; i++) {
        const ball = newBalls[i];
        if (!ball.active) continue;
        const ballFriction = ball.type === 'WHITE' ? Math.pow(CUE_BALL_FRICTION, frameScale) : friction;
        ball.vx *= ballFriction;
        ball.vy *= ballFriction;
        if (Math.hypot(ball.vx, ball.vy) < MIN_VELOCITY) { ball.vx = 0; ball.vy = 0; }
        ball.x += ball.vx * frameScale;
        ball.y += ball.vy * frameScale;
      }
      for (let i = 0; i < newBalls.length; i++) {
        const newBall = newBalls[i];
        if (!newBall.active) continue;
        if (newBall.x - BALL_RADIUS < INNER_EDGE) {
          if (!isBallInPocketMouthForCushion(newBall.x, newBall.y, 'left')) {
            newBall.x = INNER_EDGE + BALL_RADIUS;
            newBall.vx = -newBall.vx * 0.8;
          } else {
            newBall.vx *= 0.88;
            newBall.vy *= 0.88;
          }
        }
        if (newBall.x + BALL_RADIUS > TABLE_WIDTH - INNER_EDGE) {
          if (!isBallInPocketMouthForCushion(newBall.x, newBall.y, 'right')) {
            newBall.x = TABLE_WIDTH - INNER_EDGE - BALL_RADIUS;
            newBall.vx = -newBall.vx * 0.8;
          } else {
            newBall.vx *= 0.88;
            newBall.vy *= 0.88;
          }
        }
        if (newBall.y - BALL_RADIUS < INNER_EDGE) {
          if (!isBallInPocketMouthForCushion(newBall.x, newBall.y, 'top')) {
            newBall.y = INNER_EDGE + BALL_RADIUS;
            newBall.vy = -newBall.vy * 0.8;
          } else {
            newBall.vx *= 0.88;
            newBall.vy *= 0.88;
          }
        }
        if (newBall.y + BALL_RADIUS > TABLE_HEIGHT - INNER_EDGE) {
          if (!isBallInPocketMouthForCushion(newBall.x, newBall.y, 'bottom')) {
            newBall.y = TABLE_HEIGHT - INNER_EDGE - BALL_RADIUS;
            newBall.vy = -newBall.vy * 0.8;
          } else {
            newBall.vx *= 0.88;
            newBall.vy *= 0.88;
          }
        }
      }
      const impulseHitsThisSubstep = [];
      for (let pass = 0; pass < COLLISION_PASSES; pass++) {
        let hadOverlap = false;
        for (let i = 0; i < newBalls.length; i++) {
          for (let j = i + 1; j < newBalls.length; j++) {
            const b1 = newBalls[i]; const b2 = newBalls[j];
            if (!b1.active || !b2.active) continue;
            const dx = b2.x - b1.x; const dy = b2.y - b1.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const overlapAmount = BALL_RADIUS * 2 - distance;
            if (!Number.isFinite(distance) || distance < BALL_RADIUS * 2) {
              hadOverlap = true;
              let nx; let ny; let overlap;
              if (distance === 0 || !Number.isFinite(distance)) {
                nx = 1; ny = 0;
                overlap = BALL_RADIUS * 2;
              } else {
                nx = dx / distance; ny = dy / distance;
                overlap = overlapAmount;
              }
              if (distance > 0 && Number.isFinite(distance)) {
                const rvx = b1.vx - b2.vx; const rvy = b1.vy - b2.vy;
                const velAlongNormal = rvx * nx + rvy * ny;
                if (velAlongNormal > MIN_COLLISION_APPROACH) {
                  let whiteBall; let objBall;
                  if (b1.type === 'WHITE' && b2.type !== 'WHITE') { whiteBall = b1; objBall = b2; }
                  else if (b2.type === 'WHITE' && b1.type !== 'WHITE') { whiteBall = b2; objBall = b1; }
                  if (whiteBall && objBall) {
                    const cue = cueUnitAtShotStartRef.current;
                    let proj = 0;
                    if (cue) {
                      proj = (objBall.x - whiteBall.x) * cue.ux + (objBall.y - whiteBall.y) * cue.uy;
                    } else {
                      const spd = Math.hypot(whiteBall.vx, whiteBall.vy);
                      if (spd > 1e-8) {
                        proj = (objBall.x - whiteBall.x) * (whiteBall.vx / spd) + (objBall.y - whiteBall.y) * (whiteBall.vy / spd);
                      }
                    }
                    impulseHitsThisSubstep.push({ type: objBall.type, proj });
                  }
                  const jImpulse = -(1 + RESTITUTION) * velAlongNormal;
                  const impulseX = jImpulse * nx; const impulseY = jImpulse * ny;
                  b1.vx += impulseX / 2; b1.vy += impulseY / 2;
                  b2.vx -= impulseX / 2; b2.vy -= impulseY / 2;
                }
              }
              const separateBy = overlap / 2 + SEPARATION_EPSILON;
              b1.x -= separateBy * nx; b1.y -= separateBy * ny;
              b2.x += separateBy * nx; b2.y += separateBy * ny;
            }
          }
        }
        if (!hadOverlap) break;
      }
      if (!firstContactRef.current && impulseHitsThisSubstep.length > 0) {
        const phasePick = phaseForFirstContactPick(
          gamePhaseAtShotStartRef.current,
          redsRemainingRef.current,
          gamePhaseRef.current
        );
        const chosenType = pickFirstContactTypeFromImpulseHits(impulseHitsThisSubstep);
        if (chosenType) {
          firstContactRef.current = { type: chosenType, at: Date.now() };
          physicsFirstObjectTypeRef.current = chosenType;
          if (phasePick === 'COLOR' && chosenType !== 'RED') nominatedColorTypeRef.current = chosenType;
        }
      }
      for (let i = 0; i < newBalls.length; i++) {
        const newBall = newBalls[i];
        if (!newBall.active) continue;
        const speed = Math.hypot(newBall.vx, newBall.vy);
        if (speed > MAX_BALL_SPEED) {
          const s = MAX_BALL_SPEED / speed;
          newBall.vx *= s;
          newBall.vy *= s;
        }
        clampActiveBallToPlayArea(newBall);
      }
    }

    const whiteEnd = newBalls.find(b => b.type === 'WHITE' && b.active);
    const whiteSpeed = whiteEnd ? Math.hypot(whiteEnd.vx, whiteEnd.vy) : 0;
    if (!firstContactRef.current && whiteEnd && whiteSpeed > MIN_VELOCITY) {
      const phasePick = phaseForFirstContactPick(
        gamePhaseAtShotStartRef.current,
        redsRemainingRef.current,
        gamePhaseRef.current
      );
      const pick = pickGeometricFirstCueContact(whiteEnd, newBalls);
      if (pick) {
        firstContactRef.current = { type: pick.type, at: Date.now() };
        physicsFirstObjectTypeRef.current = pick.type;
        if (phasePick === 'COLOR' && pick.type !== 'RED') nominatedColorTypeRef.current = pick.type;
      }
    }

    const anyMoving = newBalls.some(ball => ball.active && (ball.vx !== 0 || ball.vy !== 0));

    // Diagnostic: flag frames where per-frame travel exceeds the capture window.
    // Total per-frame displacement is v * (deltaMs / BASE_FRAME_MS); once that is
    // above POCKET_CAPTURE_RADIUS the end-point test alone can miss a pot and the
    // swept check in checkPockets is what saves it.
    if (anyMoving) {
      const frameTravelScale = safeDeltaMs / BASE_FRAME_MS;
      let fastestType = null;
      let fastestTravel = 0;
      for (const ball of newBalls) {
        if (!ball.active) continue;
        const travel = Math.hypot(ball.vx, ball.vy) * frameTravelScale;
        if (travel > fastestTravel) { fastestTravel = travel; fastestType = ball.type; }
      }
      if (fastestTravel > POCKET_CAPTURE_RADIUS) {
        console.warn(
          '[Snooker] Tunnel risk this frame —', fastestType,
          'travels', fastestTravel.toFixed(1) + 'px vs capture radius', POCKET_CAPTURE_RADIUS,
          '| deltaMs:', safeDeltaMs.toFixed(1),
          '(swept capture will cover this)'
        );
      }
    }

    updateGameState({ balls: newBalls, ballsMoving: anyMoving });
  }, [isAuthoritative, updateGameState]);

  const buildGameState = useCallback(() => {
    return {
      balls: ballsRef.current.map(b => ({ id: b.id, type: b.type, color: b.color, x: b.x, y: b.y, vx: b.vx, vy: b.vy, active: b.active, value: b.value })),
      currentPlayer: currentPlayerRef.current,
      scores: { player1: scoresRef.current.player1, player2: scoresRef.current.player2 },
      gamePhase: gamePhaseRef.current,
      redsRemaining: redsRemainingRef.current,
      nextRequiredBall: nextRequiredBallRef.current,
      ballsMoving: ballsMovingRef.current,
      message: messageRef.current,
      tablePopupEvent: tablePopupEventRef.current,
      turnContinues: turnContinuesRef.current,
      shotInProgress: shotInProgressRef.current,
      shotTimeLeft: shotTimeLeftRef.current,
      turnStartAt: turnStartAtRef.current,
      missedTurns: { ...missedTurnsRef.current },
      gameEndReason: gameEndReasonRef.current,
      timeoutWinner: timeoutWinnerRef.current,
    };
  }, []);

  // AI shot for Player 2 in computer mode
  const executeAIShot = useCallback(() => {
    displayAimRef.current = null;
    if (!isAuthoritative) { aiShotScheduledRef.current = false; return; }
    if (currentPlayerRef.current !== 2) { aiShotScheduledRef.current = false; return; }
    if (shotInProgressRef.current || ballsMovingRef.current || shotResolvedRef.current) { aiShotScheduledRef.current = false; return; }
    if (pendingSwitchTimeoutRef.current) { aiShotScheduledRef.current = false; return; }
    const whiteBall = ballsRef.current.find(b => b.type === 'WHITE' && b.active);
    if (!whiteBall) { aiShotScheduledRef.current = false; return; }
    if (gamePhaseRef.current === 'GAME_OVER') { aiShotScheduledRef.current = false; return; }
    if (countActiveObjectBalls(ballsRef.current) === 0) {
      checkGameEnd();
      aiShotScheduledRef.current = false;
      return;
    }
    const phase = gamePhaseRef.current;
    const required = nextRequiredBallRef.current;
    const activeBalls = ballsRef.current.filter(b => b.active && b.type !== 'WHITE');
    let targetBall = null;
    if (phase === 'RED') {
      targetBall = activeBalls.find(b => b.type === 'RED');
    } else if (phase === 'COLOR') {
      targetBall = activeBalls.find(b => b.type !== 'RED') || activeBalls[0];
    } else if (phase === 'COLORS_ONLY') {
      targetBall = activeBalls.find(b => b.type === required) || activeBalls[0];
    }
    if (!targetBall) {
      const dx = TABLE_WIDTH * 0.5 - whiteBall.x;
      const dy = TABLE_HEIGHT * 0.5 - whiteBall.y;
      const dist = Math.hypot(dx, dy) || 1;
      const angle = Math.atan2(dy, dx);
      const power = aiPowerFromDistance(dist);
      const vx = Math.cos(angle) * power * 10;
      const vy = Math.sin(angle) * power * 10;
      resetPerShotRuleRefs();
      gamePhaseAtShotStartRef.current = gamePhaseRef.current;
      nextRequiredBallAtShotStartRef.current = nextRequiredBallRef.current;
      shooterPlayerNumberAtShotStartRef.current = currentPlayerRef.current;
      scoresAtShotStartRef.current = { player1: scoresRef.current.player1, player2: scoresRef.current.player2 };
      shotResolvedRef.current = false;
      {
        const wx = vx * CUE_BALL_VELOCITY_SCALE;
        const wy = vy * CUE_BALL_VELOCITY_SCALE;
        const vlen = Math.hypot(wx, wy);
        cueUnitAtShotStartRef.current = vlen > 1e-8 ? { ux: wx / vlen, uy: wy / vlen } : null;
      }
      shotStartWhitePosRef.current = { x: whiteBall.x, y: whiteBall.y };
      lockFirstContactFromShotStart(
        shotStartWhitePosRef.current,
        cueUnitAtShotStartRef.current,
        ballsRef.current,
        gamePhaseAtShotStartRef.current
      );
      const newBalls = ballsRef.current.map(ball => {
        if (ball.type === 'WHITE' && ball.active) return { ...ball, vx: vx * CUE_BALL_VELOCITY_SCALE, vy: vy * CUE_BALL_VELOCITY_SCALE };
        return ball;
      });
      updateGameState({ balls: newBalls, shotInProgress: true, ballsMoving: true });
      return;
    }
    const pocket = nearestPocket(targetBall.x, targetBall.y);
    const pdx = pocket.x - targetBall.x;
    const pdy = pocket.y - targetBall.y;
    const pLen = Math.hypot(pdx, pdy) || 1;
    const ux = pdx / pLen;
    const uy = pdy / pLen;
    const ghostX = targetBall.x - ux * BALL_RADIUS * 2;
    const ghostY = targetBall.y - uy * BALL_RADIUS * 2;
    const ignore = new Set(['white', targetBall.id]);
    const useGhost = !aiGhostShotBlocked(whiteBall.x, whiteBall.y, ghostX, ghostY, ignore, ballsRef.current);
    let aimX; let aimY;
    if (useGhost) {
      aimX = ghostX;
      aimY = ghostY;
    } else {
      aimX = targetBall.x;
      aimY = targetBall.y;
    }
    const dx = aimX - whiteBall.x;
    const dy = aimY - whiteBall.y;
    const dist = Math.hypot(dx, dy) || 1;
    const angle = Math.atan2(dy, dx);
    const power = aiPowerFromDistance(dist);
    const vx = Math.cos(angle) * power * 10;
    const vy = Math.sin(angle) * power * 10;
    resetPerShotRuleRefs();
    gamePhaseAtShotStartRef.current = gamePhaseRef.current;
    nextRequiredBallAtShotStartRef.current = nextRequiredBallRef.current;
    shooterPlayerNumberAtShotStartRef.current = currentPlayerRef.current;
    scoresAtShotStartRef.current = { player1: scoresRef.current.player1, player2: scoresRef.current.player2 };
    shotResolvedRef.current = false;
    {
      const wx = vx * CUE_BALL_VELOCITY_SCALE;
      const wy = vy * CUE_BALL_VELOCITY_SCALE;
      const vlen = Math.hypot(wx, wy);
      cueUnitAtShotStartRef.current = vlen > 1e-8 ? { ux: wx / vlen, uy: wy / vlen } : null;
    }
    shotStartWhitePosRef.current = { x: whiteBall.x, y: whiteBall.y };
    lockFirstContactFromShotStart(
      shotStartWhitePosRef.current,
      cueUnitAtShotStartRef.current,
      ballsRef.current,
      gamePhaseAtShotStartRef.current
    );
    const newBalls = ballsRef.current.map(ball => {
      if (ball.type === 'WHITE' && ball.active) return { ...ball, vx: vx * CUE_BALL_VELOCITY_SCALE, vy: vy * CUE_BALL_VELOCITY_SCALE };
      return ball;
    });
    updateGameState({ balls: newBalls, shotInProgress: true, ballsMoving: true });
  }, [isAuthoritative, updateGameState, checkGameEnd, resetPerShotRuleRefs, lockFirstContactFromShotStart]);

  const applyRemoteState = useCallback((remoteState) => {
    if (!remoteState) return;
    // The very first snapshot distinguishes "the match just ended in front of me"
    // from "I loaded into a match that was already over" (refresh / spectator join).
    if (!firstRemoteStateSeenRef.current) {
      firstRemoteStateSeenRef.current = true;
      loadedIntoFinishedMatchRef.current = remoteState.gamePhase === 'GAME_OVER';
    }
    const updates = {};
    const prevBalls = ballsRef.current;
    const hasBallsChanged = remoteState.balls && (prevBalls.length !== remoteState.balls.length || prevBalls.some((b, i) => {
      const rb = remoteState.balls[i];
      return Math.abs(b.x - rb.x) > 0.1 || Math.abs(b.y - rb.y) > 0.1 || b.active !== rb.active;
    }));
    if (hasBallsChanged) {
      const now = Date.now();
      const intervalMs = lastRemoteUpdateRef.current ? now - lastRemoteUpdateRef.current : null;
      lastRemoteUpdateRef.current = now;
      const maxSpeed = remoteState.balls ? remoteState.balls.reduce((max, b) => {
        const speed = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
        return speed > max ? speed : max;
      }, 0) : 0;
    }
    if (hasBallsChanged) updates.balls = remoteState.balls;
    if (remoteState.currentPlayer !== undefined && remoteState.currentPlayer !== currentPlayerRef.current) updates.currentPlayer = remoteState.currentPlayer;
    if (remoteState.scores && (scoresRef.current.player1 !== remoteState.scores.player1 || scoresRef.current.player2 !== remoteState.scores.player2)) updates.scores = remoteState.scores;
    if (remoteState.gamePhase && remoteState.gamePhase !== gamePhaseRef.current) updates.gamePhase = remoteState.gamePhase;
    if (remoteState.redsRemaining !== undefined && remoteState.redsRemaining !== redsRemainingRef.current) updates.redsRemaining = remoteState.redsRemaining;
    if (remoteState.nextRequiredBall && remoteState.nextRequiredBall !== nextRequiredBallRef.current) updates.nextRequiredBall = remoteState.nextRequiredBall;
    if (remoteState.ballsMoving !== undefined && remoteState.ballsMoving !== ballsMovingRef.current) updates.ballsMoving = remoteState.ballsMoving;
    if (remoteState.message && remoteState.message !== messageRef.current) updates.message = remoteState.message;
    if ((remoteState.tablePopupEvent?.id || null) !== (tablePopupEventRef.current?.id || null)) updates.tablePopupEvent = remoteState.tablePopupEvent || null;
    if (remoteState.turnContinues !== undefined && remoteState.turnContinues !== turnContinuesRef.current) updates.turnContinues = remoteState.turnContinues;
    if (remoteState.shotInProgress !== undefined && remoteState.shotInProgress !== shotInProgressRef.current) updates.shotInProgress = remoteState.shotInProgress;
    if (!isHostRef.current) {
      if (remoteState.turnStartAt) {
        turnStartAtRef.current = remoteState.turnStartAt;
        setTurnStartAtState(remoteState.turnStartAt);
        shotExpiredHandledRef.current = false;
        const remaining = computeRemainingShotTime(remoteState.turnStartAt);
        if (remaining !== shotTimeLeftRef.current) updates.shotTimeLeft = remaining;
      } else if (remoteState.shotTimeLeft !== undefined) {
        shotExpiredHandledRef.current = false;
        turnStartAtRef.current = Date.now() - (SHOT_TIME_LIMIT - remoteState.shotTimeLeft) * 1000;
        if (remoteState.shotTimeLeft !== shotTimeLeftRef.current) {
          updates.shotTimeLeft = remoteState.shotTimeLeft;
        }
      }
    } else if (
      remoteState.shotTimeLeft !== undefined &&
      remoteState.shotTimeLeft !== shotTimeLeftRef.current
    ) {
      updates.shotTimeLeft = remoteState.shotTimeLeft;
    }
    const remoteMissed = remoteState.missedTurns ?? { player1: 0, player2: 0 };
    if (
      remoteMissed.player1 !== missedTurnsRef.current.player1 ||
      remoteMissed.player2 !== missedTurnsRef.current.player2
    ) {
      updates.missedTurns = { player1: remoteMissed.player1 ?? 0, player2: remoteMissed.player2 ?? 0 };
    }
    if (remoteState.gameEndReason !== undefined && remoteState.gameEndReason !== gameEndReasonRef.current) {
      updates.gameEndReason = remoteState.gameEndReason;
    }
    if (remoteState.timeoutWinner !== undefined && remoteState.timeoutWinner !== timeoutWinnerRef.current) {
      updates.timeoutWinner = remoteState.timeoutWinner;
    }
    if (Object.keys(updates).length > 0) updateGameState(updates);
  }, [updateGameState]);

  const resetRoomSessionState = useCallback((showLandingNext = true) => {
    clearPersistedRoomCode();
    roomIdRef.current = null;
    setRoomId(null);
    setRoomCode('');
    setRoomStatus('offline');
    setRoomError('');
    setRoomCodeInput('');
    setIsHost(false);
    setGuestId(null);
    setGameStarted(false);
    setPresence({ hostLastSeen: null, guestLastSeen: null });
    setShowLanding(showLandingNext);
  }, []);

  const leaveRoom = useCallback(async (options = {}) => {
    const { showLandingNext = true } = options;
    if (!roomId) return;
    const roomRef = ref(rtdb, `rooms/${roomId}`);
    const clientId = clientIdRef.current;
    if (!isHost && guestId === clientId) {
      await update(roomRef, { guestId: null, guestLastSeen: null, updatedAt: serverTimestamp() }).catch(() => {});
    }
    resetRoomSessionState(showLandingNext);
  }, [roomId, isHost, guestId, resetRoomSessionState]);

  const leaveTournamentMatch = useCallback(async () => {
    if (roomId) {
      const roomRef = ref(rtdb, `rooms/${roomId}`);
      const clientId = clientIdRef.current;
      if (!isHost && guestId === clientId) {
        await update(roomRef, { guestId: null, guestLastSeen: null, updatedAt: serverTimestamp() }).catch(() => {});
      }
      resetRoomSessionState(false);
    }
    if (urlConfig.tournamentId) {
      window.location.href = `/tournament-structure/${urlConfig.tournamentId}`;
    }
  }, [roomId, isHost, guestId, resetRoomSessionState, urlConfig.tournamentId]);

  const forfeitTournamentMatch = useCallback(async () => {
    if (!roomId || !isTournamentMatch) return;

    const forfeitingPlayer = isHost ? 1 : 2;
    const winner = forfeitingPlayer === 1 ? 2 : 1;
    const winnerLabel = winner === 1 ? player1Name : player2Name;
    const forfeitingLabel = forfeitingPlayer === 1 ? player1Name : player2Name;

    const forfeitState = {
      ...buildGameState(),
      gamePhase: 'GAME_OVER',
      gameEndReason: 'abandonment',
      timeoutWinner: winner,
      ballsMoving: false,
      shotInProgress: false,
      turnContinues: false,
      message: `GAME OVER! ${winnerLabel} wins — ${forfeitingLabel} left the match`,
    };

    gameOverNotifiedRef.current = true;
    applyRemoteState(forfeitState);

    if (rtdb) {
      await update(ref(rtdb, `rooms/${roomId}`), {
        gameState: forfeitState,
        matchForfeited: {
          winnerPlayer: winner,
          forfeitedBy: forfeitingPlayer,
          at: Date.now(),
        },
        updatedAt: serverTimestamp(),
      }).catch(() => {});
    }

    const winnerSeatColor = resolveWinnerSeatColor(winner, forfeitingPlayer, urlConfig.color);
    await notifySnookerWinnerApi(
      roomId,
      winnerSeatColor,
      'multiplayer',
      'abandonment',
      urlConfig.tournamentId,
      { skipRedirect: true }
    ).catch(() => {});

    setLeaveConfirmModal(null);
    if (urlConfig.tournamentId) {
      window.location.href = `/tournament-structure/${urlConfig.tournamentId}`;
    }
  }, [
    roomId,
    isTournamentMatch,
    isHost,
    player1Name,
    player2Name,
    buildGameState,
    applyRemoteState,
    urlConfig.color,
    urlConfig.tournamentId,
  ]);

  const handleTournamentLeaveClick = useCallback(() => {
    if (!isTournamentMatch) return;
    // Spectators have no seat to forfeit — just exit back to the bracket.
    if (isSpectator) {
      if (urlConfig.tournamentId) {
        window.location.href = `/tournament-structure/${urlConfig.tournamentId}`;
      }
      return;
    }
    if (gameStarted && gamePhase !== 'GAME_OVER') {
      setLeaveConfirmModal('forfeit');
      return;
    }
    setLeaveConfirmModal('lobby');
  }, [isTournamentMatch, gameStarted, gamePhase, isSpectator, urlConfig.tournamentId]);

  const confirmTournamentLeave = useCallback(async () => {
    if (leaveConfirmModal === 'forfeit') {
      await forfeitTournamentMatch();
      return;
    }
    setLeaveConfirmModal(null);
    await leaveTournamentMatch();
  }, [leaveConfirmModal, forfeitTournamentMatch, leaveTournamentMatch]);

  const handleGameOverModalOk = useCallback(async () => {
    setGameOverModal(null);
    if (isTournamentMatch && urlConfig.tournamentId) {
      await leaveTournamentMatch();
      return;
    }
    await leaveRoom();
    setShowLanding(true);
    rejoinAttemptedRef.current = false;
    gameOverNotifiedRef.current = false;
  }, [leaveRoom, leaveTournamentMatch, isTournamentMatch, urlConfig.tournamentId]);

  /**
   * Post-match exit. The match is already decided, so this only navigates —
   * no forfeit prompt, unlike the in-play "Leave game" action.
   */
  const handleBackAfterMatchEnd = useCallback(async () => {
    if (isTournamentMatch && urlConfig.tournamentId) {
      window.location.href = `/tournament-structure/${urlConfig.tournamentId}`;
      return;
    }
    await leaveRoom();
    setShowLanding(true);
    rejoinAttemptedRef.current = false;
    gameOverNotifiedRef.current = false;
  }, [isTournamentMatch, urlConfig.tournamentId, leaveRoom]);

  async function startGame() {
    if (!roomId || !rtdb) return;
    try {
      if (isAuthoritative) {
        resetShotTimer();
      }
      await update(ref(rtdb, `rooms/${roomId}`), { gameStarted: true, updatedAt: serverTimestamp() });
      setGameStarted(true);
      if (isAuthoritative) {
        lastSyncTimeRef.current = 0;
        const gameState = buildGameState();
        await update(ref(rtdb, `rooms/${roomId}`), { gameState, updatedAt: serverTimestamp() }).catch(() => {});
      }
      notifySnookerLiveGameStart(roomId, [
        { name: hostName || 'Player 1' },
        { name: guestName || 'Player 2' }
      ]).catch(() => {});
    } catch (e) {
      setRoomError('Failed to start game.');
    }
  }

  function persistRoomCode() {
    // No browser caching of gameId/roomCode; rely only on API and URL.
  }

  function clearPersistedRoomCode() {
    // No-op: no browser caching.
  }

  // Spectator / View mode: attach to a room READ-ONLY. Never create the room,
  // never claim host/guest, never write any state. Just subscribe (onValue
  // listener keyed on roomId handles applyRemoteState since isHost stays false).
  async function attachAsSpectator(code) {
    if (!code) return;
    setRoomError('');
    setRoomStatus('connecting');
    try {
      const roomRef = ref(rtdb, `rooms/${code}`);
      const snapshot = await get(roomRef);
      if (!snapshot.exists()) {
        // Match room not created yet (e.g. live window but players haven't started).
        setRoomError('The match has not started yet. Please check back once it is live.');
        setRoomStatus('offline');
        return;
      }
      const data = snapshot.val();
      // Read-only: do NOT set isHost/guestId or write guest seat.
      setIsHost(false);
      setGuestId(data.guestId || null);
      setRoomId(code);
      setRoomCode(code);
      setRoomStatus('connected');
      setGameStarted(!!data.gameStarted);
      setGameMode(data.gameMode === 'computer' ? 'computer' : 'multiplayer');
      setHostName(data.hostName || 'Player 1');
      setGuestName(data.guestName || 'Player 2');
      setHostSeatColor(data.hostSeatColor || null);
      setGuestSeatColor(data.guestSeatColor || null);
      if (data.gameState) applyRemoteState(data.gameState);
      setShowLanding(false);
    } catch (error) {
      setRoomError('Unable to load match. Check Firebase config.');
      setRoomStatus('offline');
    }
  }

  async function joinRoomByCode(code) {
    const clientId = clientIdRef.current;
    if (!clientId) return;
    if (!code) return;
    setRoomError(''); setRoomStatus('connecting');
    try {
      const roomRef = ref(rtdb, `rooms/${code}`);
      const snapshot = await get(roomRef);
      if (!snapshot.exists()) {
        // First player with this roomCode (e.g. from tournament URL): create room as host so both see lobby
        const hostDisplayName = (urlConfig.name && String(urlConfig.name).trim()) || (() => { try { const s = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null; return (s ? JSON.parse(s) : {}).name || 'Player 1'; } catch (e) { return 'Player 1'; } })();
        const initialState = buildGameState();
        const seatColor = (urlConfig.color && String(urlConfig.color).toLowerCase() === 'white') ? 'white' : 'black';
        await set(roomRef, {
          code,
          hostId: clientId,
          guestId: null,
          hostName: hostDisplayName,
          guestName: null,
          hostSeatColor: seatColor,
          guestSeatColor: null,
          gameMode: 'multiplayer',
          gameStarted: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          hostLastSeen: serverTimestamp(),
          guestLastSeen: null,
          gameState: initialState,
        });
        setIsHost(true);
        setGuestId(null);
        setHostSeatColor(seatColor);
        setGuestSeatColor(null);
        setRoomId(code);
        setRoomCode(code);
        setRoomStatus('connected');
        setRoomCodeInput('');
        setGameStarted(false);
        setHostName(hostDisplayName);
        setGuestName('');
        setShowLanding(false);
        setGameMode('multiplayer');
        persistRoomCode(code);
        return;
      }
      const data = snapshot.val();
      if (data.guestId && data.guestId !== clientId && data.hostId !== clientId) { setRoomError('Room is full.'); setRoomStatus('offline'); return; }
      const joiningAsHost = data.hostId === clientId;
      setIsHost(joiningAsHost); setGuestId(data.guestId || null); setRoomId(code); setRoomCode(code); setRoomStatus('connected'); setRoomCodeInput(''); setGameStarted(!!data.gameStarted); applyRemoteState(data.gameState);
      setShowLanding(false);
      setGameMode(data.gameMode === 'computer' ? 'computer' : 'multiplayer');
      persistRoomCode(code);
      const guestDisplayName = (() => { try { const s = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null; return (s ? JSON.parse(s) : {}).name || 'Player 2'; } catch (e) { return 'Player 2'; } })();
      if (!joiningAsHost && !data.guestId) {
        const hostSeat = data.hostSeatColor || 'black';
        const guestSeat = (urlConfig.color && String(urlConfig.color).toLowerCase() === 'white') ? 'white'
          : (urlConfig.color && String(urlConfig.color).toLowerCase() === 'black') ? 'black'
          : (hostSeat === 'black' ? 'white' : 'black');
        await update(roomRef, {
          guestId: clientId,
          guestName: guestDisplayName,
          guestSeatColor: guestSeat,
          guestLastSeen: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setGuestName(guestDisplayName);
        setGuestSeatColor(guestSeat);
      } else {
        setGuestName(data.guestName || '');
        setGuestSeatColor(data.guestSeatColor || null);
      }
      setHostSeatColor(data.hostSeatColor || null);
      if (joiningAsHost) { await update(roomRef, { hostLastSeen: serverTimestamp(), updatedAt: serverTimestamp() }); }
      setHostName(data.hostName || 'Player 1');
    } catch (error) { setRoomError('Unable to join room. Check Firebase config.'); setRoomStatus('offline'); }
  }

  async function createRoom() {
    const clientId = clientIdRef.current;
    if (!clientId) return;
    setRoomError(''); setRoomStatus('connecting');
    try {
      for (let attempt = 0; attempt < 5; attempt += 1) {
        const code = generateRoomCode();
        const roomRef = ref(rtdb, `rooms/${code}`);
        const snapshot = await get(roomRef);
        if (snapshot.exists()) continue;
        const initialState = buildGameState();
        const hostDisplayName = (() => { try { const s = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null; return (s ? JSON.parse(s) : {}).name || 'Player 1'; } catch (e) { return 'Player 1'; } })();
        await set(roomRef, { code, hostId: clientId, guestId: null, hostName: hostDisplayName, guestName: null, gameMode: 'multiplayer', gameStarted: false, createdAt: serverTimestamp(), updatedAt: serverTimestamp(), hostLastSeen: serverTimestamp(), guestLastSeen: null, gameState: initialState });
        setRoomId(code); setRoomCode(code); setIsHost(true); setHostName(hostDisplayName); setGuestName(''); setRoomCodeInput(''); setRoomStatus('connected');
        setShowLanding(false);
        setGameMode('multiplayer');
        persistRoomCode(code);
        return;
      }
      setRoomError('Unable to create a room. Please try again.'); setRoomStatus('offline');
    } catch (error) { setRoomError('Unable to create room. Check Firebase config.'); setRoomStatus('offline'); }
  }

  async function joinRoom() {
    const code = roomCodeInput.trim().toUpperCase();
    await joinRoomByCode(code);
  }

  async function sendPendingShot(vx, vy) {
    if (!roomId) return;
    const clientId = clientIdRef.current;
    if (!clientId) return;
    const shotId = `${Date.now()}_${clientId}`;
    const roomRef = ref(rtdb, `rooms/${roomId}`);
    try {
      await update(roomRef, { pendingShot: { shotId, by: clientId, vx, vy, createdAt: Date.now() }, updatedAt: serverTimestamp() });
    } catch (error) { setRoomError('Failed to send shot. Please retry.'); }
  }

  const syncGameState = useCallback(() => {
    if (!roomId || !isHost) return;
    const isGameOver = gamePhaseRef.current === 'GAME_OVER';
    if (isGameOver) {
      // Push the final state exactly once (bypassing the throttle below so it
      // can't be delayed by the idle/moving interval), then stop syncing.
      if (gameOverSyncedRef.current) return;
      gameOverSyncedRef.current = true;
      const gameState = buildGameState();
      update(ref(rtdb, `rooms/${roomId}`), { gameState, updatedAt: serverTimestamp() }).catch((err) => { console.error("Sync error:", err); });
      return;
    }
    gameOverSyncedRef.current = false;
    const now = Date.now();
    const interval = ballsMovingRef.current ? SYNC_MOVING_MS : SYNC_IDLE_MS;
    if (now - lastSyncTimeRef.current < interval) return;
    lastSyncTimeRef.current = now;
    if (lastSyncedPlayerRef.current !== currentPlayerRef.current) {
      lastSyncedPlayerRef.current = currentPlayerRef.current;
    }
    const gameState = buildGameState();
    update(ref(rtdb, `rooms/${roomId}`), { gameState, updatedAt: serverTimestamp() }).catch((err) => { console.error("Sync error:", err); });
  }, [roomId, isHost, buildGameState]);

  // Effects
  useEffect(() => {
    if (!roomId) return undefined;
    const roomRef = ref(rtdb, `rooms/${roomId}`);
    const unsubscribe = onValue(roomRef, snapshot => {
      if (roomIdRef.current !== roomId) return;
      if (!snapshot.exists()) { setRoomError('Room closed.'); setRoomStatus('offline'); setRoomId(null); setIsHost(false); setGuestId(null); setHostName(''); setGuestName(''); setGameStarted(false); setPresence({ hostLastSeen: null, guestLastSeen: null }); return; }
      const data = snapshot.val();
      const clientId = clientIdRef.current;
      setGuestId(data.guestId || null);
      setHostName(data.hostName || '');
      setGuestName(data.guestName || '');
      setPresence({ hostLastSeen: data.hostLastSeen || null, guestLastSeen: data.guestLastSeen || null });
      if (data.hostSeatColor) setHostSeatColor(data.hostSeatColor);
      if (data.guestSeatColor) setGuestSeatColor(data.guestSeatColor);
      if (data.lobbyTimeout) {
        setLobbyTimeout(true);
        setLobbyWinnerSeat(data.lobbyWinnerSeat || null);
      }
      if (data.code) setRoomCode(data.code);
      if (data.gameStarted) setGameStarted(true);
      if (data.gameMode === 'computer' || data.gameMode === 'multiplayer') setGameMode(data.gameMode);
      setRoomStatus('connected');
      if (isHost && data.hostId !== clientId) { if (data.hostId) { setRoomError('Host session invalid.'); leaveRoom(); return; } }
      const remoteHasGameOver = data.gameState && (
        (data.gameState.message && String(data.gameState.message).toUpperCase().includes('GAME OVER')) ||
        data.gameState.gamePhase === 'GAME_OVER'
      );
      if (data.gameState && (!isHost || remoteHasGameOver || data.matchForfeited)) {
        applyRemoteState(data.gameState);
      }
      if (isHost && data.pendingShot && data.pendingShot.by !== clientId) {
        const { shotId, vx, vy } = data.pendingShot;
        if (shotId && shotId !== lastProcessedShotRef.current) {
          lastProcessedShotRef.current = shotId;
          resetPerShotRuleRefs();
          gamePhaseAtShotStartRef.current = gamePhaseRef.current;
          nextRequiredBallAtShotStartRef.current = nextRequiredBallRef.current;
          shooterPlayerNumberAtShotStartRef.current = currentPlayerRef.current;
          scoresAtShotStartRef.current = { player1: scoresRef.current.player1, player2: scoresRef.current.player2 };
          shotResolvedRef.current = false;
          {
            const wx = vx * CUE_BALL_VELOCITY_SCALE;
            const wy = vy * CUE_BALL_VELOCITY_SCALE;
            const vlen = Math.hypot(wx, wy);
            cueUnitAtShotStartRef.current = vlen > 1e-8 ? { ux: wx / vlen, uy: wy / vlen } : null;
          }
          const whiteBall = ballsRef.current.find(b => b.type === 'WHITE' && b.active);
          shotStartWhitePosRef.current = whiteBall ? { x: whiteBall.x, y: whiteBall.y } : null;
          lockFirstContactFromShotStart(
            shotStartWhitePosRef.current,
            cueUnitAtShotStartRef.current,
            ballsRef.current,
            gamePhaseAtShotStartRef.current
          );
          const currentBalls = ballsRef.current;
          const newBalls = currentBalls.map(ball => { if (ball.type === 'WHITE' && ball.active) { return { ...ball, vx: vx * CUE_BALL_VELOCITY_SCALE, vy: vy * CUE_BALL_VELOCITY_SCALE }; } return ball; });
          updateGameState({ balls: newBalls, shotInProgress: true, ballsMoving: true });
          update(roomRef, { pendingShot: null, updatedAt: serverTimestamp() }).catch(() => {});
        }
      }
    });
    return () => unsubscribe();
  }, [roomId, isHost, leaveRoom, applyRemoteState, updateGameState, resetPerShotRuleRefs, lockFirstContactFromShotStart]);

  useEffect(() => {
    if (!roomId) return undefined;
    if (isSpectator) return undefined; // spectators never write presence
    const roomRef = ref(rtdb, `rooms/${roomId}`);
    const interval = setInterval(() => {
      const updates = isHost ? { hostLastSeen: serverTimestamp() } : { guestLastSeen: serverTimestamp() };
      update(roomRef, updates).catch(() => {});
    }, PRESENCE_UPDATE_MS);
    return () => clearInterval(interval);
  }, [roomId, isHost, isSpectator]);

  useEffect(() => {
    if (clientIdRef.current) {
      setClientIdReady(true);
      return;
    }
    const storage = window.sessionStorage;
    const existing = storage.getItem('snookerClientId');
    const newId = existing || `client_${Math.random().toString(36).slice(2, 10)}`;
    storage.setItem('snookerClientId', newId);
    clientIdRef.current = newId;
    setClientIdReady(true);
  }, []);

  useEffect(() => {
    // Hide the shared header's Back button on this page — leaving mid-game
    // should go through the in-game Leave/forfeit controls, not a plain
    // header back button that skips confirmation.
    if (updateHeader) updateHeader({ backButtonRedirect: '/', hideBackButton: true });
  }, [updateHeader]);

  // Live mode only: read theme flags from sessionStorage (same source as Ludo)
  useEffect(() => {
    if (!isLiveMode) return;
    const themeRaw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('themeData') : null;
    if (!themeRaw) return;
    try {
      const theme = JSON.parse(themeRaw);
      const multiplayer = !!(theme.multiplayer ?? theme.tournament ?? theme.snooker_tournament);
      const flags = { computer: !!theme.computer, multiplayer };
      setLiveThemeFlags(flags);
      setGameMode((prev) => {
        const onlyComputer = flags.computer && !flags.multiplayer;
        const onlyMultiplayer = flags.multiplayer && !flags.computer;
        if (onlyComputer) return 'computer';
        if (onlyMultiplayer) return 'multiplayer';
        return urlConfig.mode === 'computer' || urlConfig.mode === 'multiplayer' ? urlConfig.mode : (flags.computer ? 'computer' : 'multiplayer');
      });
    } catch (e) {
      setLiveThemeFlags({ computer: true, multiplayer: true });
    }
  }, [isLiveMode, urlConfig.mode]);

  // Live mode: check for existing game on mount (same API as Ludo – gameId is room code)
  useEffect(() => {
    if (!isLiveMode || urlConfig.roomCode || checkingExistingRef.current) return;
    checkingExistingRef.current = true;
    const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null;
    if (!stored) {
      checkingExistingRef.current = false;
      return;
    }
    const fetchExistingGame = async () => {
      try {
        const uData = JSON.parse(stored);
        const base = process.env.REACT_APP_FOLDER_LINK;
        if (!base) { checkingExistingRef.current = false; return; }
        const res = await fetch(base + '/api/check-computer-exist.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: uData.sessionId, userId: urlConfig.userId || uData.userId }),
        });
        if (!res.ok) { checkingExistingRef.current = false; return; }
        const data = await res.json();
        if (data?.success && (data.limitReached === true || Number(data.completedCount) >= 3)) {
          try {
            localStorage.setItem('ludoGameCompleted', JSON.stringify({
              allCompleted: true,
              completedCount: Number(data.completedCount) || 3,
              limitReached: true,
            }));
          } catch (e) {}
          window.location.href = '/thankyou';
          return;
        }
        const roomCode = data?.roomCode ?? data?.gameId;
        if (data?.success && data?.exists && roomCode) {
          const code = String(roomCode).trim().toUpperCase().slice(0, ROOM_CODE_MAX_URL_LENGTH);
          setShowLanding(false);
          persistRoomCode(code);
          joinRoomByCode(code);
        }
      } catch (err) {
        console.error('Snooker check existing game:', err);
      }
      checkingExistingRef.current = false;
    };
    fetchExistingGame();
  }, [isLiveMode, urlConfig.roomCode, urlConfig.userId]);

  // Rejoin: only when roomCode comes from URL (no browser-cached gameId)
  useEffect(() => {
    if (showLanding || roomId || rejoinAttemptedRef.current || !clientIdReady) return;
    const code = initialRejoinCode ? String(initialRejoinCode).trim().toUpperCase() : null;
    if (!code || !clientIdRef.current) return;
    rejoinAttemptedRef.current = true;
    if (isSpectator) {
      attachAsSpectator(code);
    } else {
      joinRoomByCode(code);
    }
  }, [showLanding, roomId, initialRejoinCode, clientIdReady]);

  // Tournament: load scheduled opponent name from bracket (before they join Firebase room)
  useEffect(() => {
    if (!isTournamentMatch || !urlConfig.tournamentId || !initialRejoinCode) return;

    const resolvedUserId =
      urlConfig.userId ||
      (() => {
        try {
          const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null;
          return stored ? JSON.parse(stored).userId : null;
        } catch (e) {
          return null;
        }
      })();

    if (!resolvedUserId) return;

    const matchGameId = String(initialRejoinCode).trim().toUpperCase();

    (async () => {
      try {
        const res = await fetch(
          process.env.REACT_APP_FOLDER_LINK + '/api/getMatchesList.php',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tournamentId: urlConfig.tournamentId,
              userId: resolvedUserId,
            }),
          }
        );
        const data = await res.json();
        for (const round of data.tournaments || []) {
          for (const match of round.matches || []) {
            const gid = String(match.gameId || '').trim().toUpperCase();
            if (gid !== matchGameId) continue;
            const players = (match.players || []).filter(
              (p) => !p.is_bye && p.userid && p.userid !== 'TBD'
            );
            const opponent = players.find((p) => p.userid !== resolvedUserId);
            if (opponent?.name) {
              setTournamentOpponentName(opponent.name);
              return;
            }
          }
        }
      } catch (err) {
        console.error('Failed to load tournament opponent name', err);
      }
    })();
  }, [isTournamentMatch, urlConfig.tournamentId, urlConfig.userId, initialRejoinCode]);

  /**
   * Tournament: restore the result popup when loading into a finished match.
   * The backend match record is the durable source — it survives a refresh even
   * if the Firebase room is already gone, and it works for spectators too.
   */
  useEffect(() => {
    if (!isTournamentMatch || !urlConfig.tournamentId || !initialRejoinCode) return;
    if (matchResultRestoredRef.current) return;

    const resolvedUserId =
      urlConfig.userId ||
      (() => {
        try {
          const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null;
          return stored ? JSON.parse(stored).userId : null;
        } catch (e) {
          return null;
        }
      })();

    const matchGameId = String(initialRejoinCode).trim().toUpperCase();

    (async () => {
      try {
        const res = await fetch(process.env.REACT_APP_FOLDER_LINK + '/api/getMatchesList.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tournamentId: urlConfig.tournamentId, userId: resolvedUserId }),
        });
        const data = await res.json();
        for (const round of data.tournaments || []) {
          for (const match of round.matches || []) {
            if (String(match.gameId || '').trim().toUpperCase() !== matchGameId) continue;
            if (String(match.status || '') !== 'completed') return;

            matchResultRestoredRef.current = true;
            // The frame is already decided; stop the live handler re-deciding it.
            gameOverNotifiedRef.current = true;

            const players = (match.players || []).filter(
              (p) => !p.is_bye && p.userid && p.userid !== 'TBD'
            );
            const winnerEntry = players.find((p) => String(p.userid) === String(match.winnerId));
            const winnerName = winnerEntry?.name || 'Winner';
            const byAbandonment = String(match.outcome || '') === 'abandonment';
            const viewerWon =
              !isSpectator && resolvedUserId && String(match.winnerId) === String(resolvedUserId);

            const reasonLine = byAbandonment
              ? `${winnerName} won — opponent did not complete the match`
              : `${winnerName} won the frame`;

            if (isSpectator || !resolvedUserId) {
              setGameOverModal({
                variant: 'neutral',
                title: 'Match ended',
                scoreLine: reasonLine,
                body: byAbandonment
                  ? 'The match ended by abandonment.'
                  : 'This match is already complete.',
              });
              return;
            }

            setGameOverModal({
              variant: viewerWon ? 'win' : 'loss',
              title: viewerWon ? "Congratulations! You've won!" : 'You lost',
              scoreLine: reasonLine,
              body: byAbandonment
                ? (viewerWon
                  ? 'Your opponent did not complete the match. You advance.'
                  : 'You did not complete the match.')
                : (viewerWon
                  ? 'You won this match.'
                  : `${winnerName} takes this match.`),
            });
            return;
          }
        }
      } catch (err) {
        console.error('Failed to restore Snooker match result', err);
      }
    })();
  }, [isTournamentMatch, urlConfig.tournamentId, urlConfig.userId, initialRejoinCode, isSpectator]);

  useEffect(() => {
    tournamentAutoStartAttemptedRef.current = false;  }, [roomId]);

  // Tournament: auto-start when both players are in the room (host only)
  useEffect(() => {
    if (!isTournamentMatch || !roomId || gameStarted) return;
    if (!guestId || !isHost) return;
    if (tournamentAutoStartAttemptedRef.current) return;
    tournamentAutoStartAttemptedRef.current = true;
    startGame();
  }, [isTournamentMatch, roomId, gameStarted, guestId, isHost]);

  // Tournament: mark player present in MySQL once per game join
  useEffect(() => {
    if (isSpectator) return; // spectators are not players; never mark present
    if (!isTournamentMatch || !roomId || !urlConfig.tournamentId) return;
    const resolvedUserId = urlConfig.userId || (() => {
      try {
        const s = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null;
        return s ? JSON.parse(s).userId : null;
      } catch (e) { return null; }
    })();
    if (!resolvedUserId) return;
    const key = `${roomId}:${resolvedUserId}`;
    if (presentMarkedRef.current[key]) return;
    presentMarkedRef.current[key] = true;
    markTournamentPlayerPresentApi(roomId, resolvedUserId);
  }, [isTournamentMatch, roomId, urlConfig.tournamentId, urlConfig.userId]);

  // Tournament lobby: 10-minute timer when alone; present player wins by default
  useEffect(() => {
    // Spectators never run the lobby timer or claim default wins
    if (isSpectator) return undefined;

    const isLobbyTimerActive =
      isTournamentMatch &&
      roomId &&
      !guestId &&
      !gameStarted &&
      !lobbyTimeout;

    if (!isLobbyTimerActive) {
      if (lobbyTimerIntervalRef.current) {
        clearInterval(lobbyTimerIntervalRef.current);
        lobbyTimerIntervalRef.current = null;
      }
      setLobbyTimerSeconds(LOBBY_WAIT_SECONDS);
      return undefined;
    }

    if (lobbyTimerIntervalRef.current) return undefined;
    setLobbyTimerSeconds(LOBBY_WAIT_SECONDS);

    const applyLobbyDefaultWin = () => {
      if (!isAuthoritative || !roomId || !rtdb) return;
      const roomRef = ref(rtdb, `rooms/${roomId}`);
      runTransaction(roomRef, (current) => {
        if (!current || current.gameStarted || current.lobbyTimeout) return current;
        if (current.guestId) return current;
        const seat = current.hostSeatColor || urlConfig.color || 'black';
        return {
          ...current,
          lobbyTimeout: true,
          lobbyWinnerSeat: seat,
          gameStarted: false,
        };
      }, { applyLocally: false }).then((result) => {
        const val = result.snapshot.val();
        if (val?.lobbyTimeout && val?.lobbyWinnerSeat && !lobbyWinNotifiedRef.current) {
          lobbyWinNotifiedRef.current = true;
          notifySnookerWinnerApi(roomId, val.lobbyWinnerSeat, 'multiplayer', 'win', urlConfig.tournamentId);
        }
      }).catch((err) => {
        console.error('[Snooker] Lobby default win failed:', err);
      });
    };

    lobbyTimerIntervalRef.current = setInterval(() => {
      setLobbyTimerSeconds((prev) => {
        if (prev <= 1) {
          if (lobbyTimerIntervalRef.current) {
            clearInterval(lobbyTimerIntervalRef.current);
            lobbyTimerIntervalRef.current = null;
          }
          setTimeout(applyLobbyDefaultWin, 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (lobbyTimerIntervalRef.current) {
        clearInterval(lobbyTimerIntervalRef.current);
        lobbyTimerIntervalRef.current = null;
      }
    };
  }, [isTournamentMatch, roomId, guestId, gameStarted, lobbyTimeout, isAuthoritative, urlConfig.color, urlConfig.tournamentId, isSpectator]);

  // Lobby timeout: show game-over modal for all clients
  useEffect(() => {
    if (!lobbyTimeout || !lobbyWinnerSeat || gameOverNotifiedRef.current) return;
    gameOverNotifiedRef.current = true;
    const mySeat = isHost ? (hostSeatColor || urlConfig.color) : (guestSeatColor || urlConfig.color);
    const normalizedMy = String(mySeat || '').toLowerCase();
    const normalizedWin = String(lobbyWinnerSeat || '').toLowerCase();
    const isWin = normalizedMy && normalizedWin && normalizedMy === normalizedWin;
    const winnerLabel = normalizedWin === 'black' ? player1Name : normalizedWin === 'white' ? player2Name : 'Winner';
    setGameOverModal({
      variant: isWin ? 'win' : 'loss',
      title: isWin ? 'You win!' : 'Match ended',
      scoreLine: `${winnerLabel} wins by default`,
      body: isWin
        ? 'Your opponent did not join within 10 minutes.'
        : 'The waiting period expired. The present player wins by default.',
    });
  }, [lobbyTimeout, lobbyWinnerSeat, isHost, hostSeatColor, guestSeatColor, urlConfig.color, player1Name, player2Name]);

  // Game over: notify backend in background, show modal, then leave room on OK (use gamePhase so guest gets it from sync in multiplayer)
  useEffect(() => {
    if (!roomId || gameOverNotifiedRef.current) return;
    if (lobbyTimeout) return;
    const isGameOver = gamePhase === 'GAME_OVER' || (message && String(message).toUpperCase().includes('GAME OVER'));
    if (!isGameOver) return;
    gameOverNotifiedRef.current = true;
    const p1 = scores.player1;
    const p2 = scores.player2;
    const isAbandonmentEnd = gameEndReason === 'abandonment' && timeoutWinner;
    const winner = isAbandonmentEnd
      ? timeoutWinner
      : (p1 > p2 ? 1 : p2 > p1 ? 2 : null);
    const userId = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('userData')
      ? (() => { try { return JSON.parse(sessionStorage.getItem('userData')).userId; } catch (e) { return null; } })()
      : null;

    const localPN = gameMode === 'computer' ? 1 : (roomId ? (isHost ? 1 : 2) : currentPlayer);
    const isLeaveForfeit = message && String(message).includes('left the match');
    const outcome = isAbandonmentEnd ? 'abandonment' : 'win';

    // Winner userId for ExtraMile report: current user if they won; else computer / null
    let winnerId = null;
    if (winner != null) {
      if (gameMode === 'computer') {
        winnerId = winner === 1 ? userId : 'computer';
      } else {
        winnerId = winner === localPN ? userId : null;
      }
    }

    if (gameMode === 'multiplayer' && isAuthoritative && winner && !isLeaveForfeit) {
      // Already-finished match (refresh): the winner was recorded when it ended.
      if (!loadedIntoFinishedMatchRef.current) {
        const winnerSeatColor = resolveWinnerSeatColor(winner, localPN, urlConfig.color);
        notifySnookerWinnerApi(
          roomId,
          winnerSeatColor,
          'multiplayer',
          outcome,
          urlConfig.tournamentId,
          // Keep the result popup on screen; leaving is driven by the OK button.
          { skipRedirect: true }
        ).catch(() => {});
        // Live casual multiplayer: also save + send ExtraMile report (like Ludo)
        if (isLiveMode) {
          notifySnookerLiveGameEnd(roomId, winnerId, outcome).catch(() => {});
        }
      }
    } else if (gameMode !== 'multiplayer' && !loadedIntoFinishedMatchRef.current) {
      notifySnookerLiveGameEnd(roomId, winnerId, outcome).catch(() => {});
    }

    if (isAbandonmentEnd) {
      const winnerLabel = timeoutWinner === 1 ? player1Name : player2Name;
      const loserLabel = timeoutWinner === 1 ? player2Name : player1Name;
      const isWin = !isSpectator && timeoutWinner === localPN;
      if (isSpectator) {
        setGameOverModal({
          variant: 'neutral',
          title: 'Match ended',
          scoreLine: isLeaveForfeit
            ? `${winnerLabel} wins — opponent left`
            : `${winnerLabel} wins — opponent abandoned`,
          body: isLeaveForfeit
            ? `${loserLabel} left the match, so ${winnerLabel} wins by abandonment.`
            : `${loserLabel} used all 3 shot retries, so ${winnerLabel} wins by abandonment.`,
        });
        return;
      }
      setGameOverModal({
        variant: isWin ? 'win' : 'loss',
        title: isWin ? "You win!" : 'You lost',
        scoreLine: isLeaveForfeit
          ? `${winnerLabel} wins — opponent left`
          : `${winnerLabel} wins — opponent abandoned`,
        body: isLeaveForfeit
          ? (isWin
            ? 'Your opponent left the match. You win by abandonment.'
            : 'You left the match and forfeited this frame.')
          : (isWin
            ? 'Your opponent used all 3 shot retries and forfeited.'
            : `You missed 3 shots. ${winnerLabel} wins by abandonment.`),
      });
      return;
    }

    const winnerLabel = p1 > p2 ? player1Name : p2 > p1 ? player2Name : `${player1Name} / ${player2Name}`;
    if (p1 === p2) {
      setGameOverModal({
        variant: 'neutral',
        title: 'Game Over',
        scoreLine: `${p1} – ${p2}`,
        body: 'The frame ended with level scores.',
      });
    } else if (isSpectator) {
      setGameOverModal({
        variant: 'neutral',
        title: 'Match ended',
        scoreLine: `${winnerLabel} wins ${p1} – ${p2}`,
        body: `${winnerLabel} takes the frame.`,
      });
    } else {
      const isWin = winner === localPN;
      setGameOverModal({
        variant: isWin ? 'win' : 'loss',
        title: isWin ? "Congratulations! You've won!" : 'You lost',
        scoreLine: `${winnerLabel} wins ${p1} – ${p2}`,
        body: isWin
          ? 'You won the frame—excellent snooker.'
          : `${winnerLabel} takes the frame. Better luck next time!`,
      });
    }
  }, [gameMode, roomId, gamePhase, message, scores.player1, scores.player2, player1Name, player2Name, isHost, currentPlayer, isAuthoritative, urlConfig.color, urlConfig.tournamentId, gameEndReason, timeoutWinner, lobbyTimeout, isLiveMode, isSpectator]);

  useEffect(() => {
    if (!gameOverModal) return undefined;
    const blockEscape = (e) => {
      if (e.key === 'Escape') e.preventDefault();
    };
    window.addEventListener('keydown', blockEscape);
    return () => window.removeEventListener('keydown', blockEscape);
  }, [gameOverModal]);

  useEffect(() => {
    const timerActive =
      gameMode === 'computer'
        ? isAuthoritative
        : !!(roomId && (!isTournamentMatch || gameStarted));

    if (!timerActive) return undefined;

    const tick = () => {
      if (ballsMovingRef.current || shotInProgressRef.current) return;
      if (gamePhaseRef.current === 'GAME_OVER') return;

      let startAt = turnStartAtRef.current;
      if (!startAt) {
        if (isAuthoritative) {
          resetShotTimer();
        }
        return;
      }

      const remaining = computeRemainingShotTime(startAt);
      if (remaining !== shotTimeLeftRef.current) {
        shotTimeLeftRef.current = remaining;
        setShotTimeLeft(remaining);
      }

      if (remaining > 0) {
        shotExpiredHandledRef.current = false;
        return;
      }

      if (!isAuthoritative || shotExpiredHandledRef.current) return;
      shotExpiredHandledRef.current = true;
      handleShotTimerExpired();
    };

    tick();
    const interval = setInterval(tick, SHOT_TIMER_TICK_MS);
    return () => clearInterval(interval);
  }, [
    gameMode,
    roomId,
    isTournamentMatch,
    gameStarted,
    isAuthoritative,
    handleShotTimerExpired,
    resetShotTimer,
  ]);

  useEffect(() => {
    roomIdRef.current = roomId;
    isHostRef.current = isHost;
  }, [roomId, isHost]);

  // Computer mode: show cue stick (display aim) then AI takes shot
  useEffect(() => {
    if (gameMode !== 'computer' || currentPlayer !== 2 || ballsMoving || shotInProgress) {
      if (!ballsMoving && !shotInProgress) aiShotScheduledRef.current = false;
      return;
    }
    if (gamePhaseRef.current === 'GAME_OVER' || countActiveObjectBalls(ballsRef.current) === 0) return;
    if (aiShotScheduledRef.current) return;
    aiShotScheduledRef.current = true;
    const whiteBall = ballsRef.current.find(b => b.type === 'WHITE' && b.active);
    if (whiteBall) {
      const phase = gamePhaseRef.current;
      const required = nextRequiredBallRef.current;
      const activeBalls = ballsRef.current.filter(b => b.active && b.type !== 'WHITE');
      let targetBall = null;
      if (phase === 'RED') targetBall = activeBalls.find(b => b.type === 'RED');
      else if (phase === 'COLOR') targetBall = activeBalls.find(b => b.type !== 'RED') || activeBalls[0];
      else if (phase === 'COLORS_ONLY') targetBall = activeBalls.find(b => b.type === required) || activeBalls[0];
      let angle;
      if (!targetBall) {
        const dx = TABLE_WIDTH * 0.5 - whiteBall.x;
        const dy = TABLE_HEIGHT * 0.5 - whiteBall.y;
        angle = Math.atan2(dy, dx);
      } else {
        const dx = targetBall.x - whiteBall.x;
        const dy = targetBall.y - whiteBall.y;
        angle = Math.atan2(dy, dx);
      }
      const pull = 50;
      displayAimRef.current = { x: whiteBall.x - Math.cos(angle) * pull, y: whiteBall.y - Math.sin(angle) * pull };
    }
    const t = setTimeout(() => {
      executeAIShot();
      displayAimRef.current = null;
      aiShotScheduledRef.current = false;
    }, 600);
    return () => {
      clearTimeout(t);
      aiShotScheduledRef.current = false;
    };
  }, [gameMode, currentPlayer, ballsMoving, shotInProgress, executeAIShot]);

  useEffect(() => {
    if (gameMode !== 'computer' || currentPlayer !== 2) return;
    if (ballsMoving || shotInProgress) return;
    if (pendingSwitchTimeoutRef.current || shotResolvedRef.current) return;
    if (gamePhaseRef.current === 'GAME_OVER' || countActiveObjectBalls(ballsRef.current) === 0) return;
    const watchdog = setTimeout(() => {
      if (gameMode !== 'computer' || currentPlayerRef.current !== 2) return;
      if (ballsMovingRef.current || shotInProgressRef.current) return;
      if (pendingSwitchTimeoutRef.current || shotResolvedRef.current) return;
      if (gamePhaseRef.current === 'GAME_OVER' || countActiveObjectBalls(ballsRef.current) === 0) return;
      if (!ballsRef.current.some(b => b.type === 'WHITE' && b.active)) return;
      aiShotScheduledRef.current = false;
      executeAIShot();
    }, 3200);
    return () => clearTimeout(watchdog);
  }, [gameMode, currentPlayer, ballsMoving, shotInProgress, executeAIShot]);

  const updatePhysicsRef = useRef(updatePhysics);
  const checkPocketsRef = useRef(checkPockets);
  const syncGameStateRef = useRef(syncGameState);
  const renderRef = useRef(null);

  useEffect(() => {
    updatePhysicsRef.current = updatePhysics;
    checkPocketsRef.current = checkPockets;
    syncGameStateRef.current = syncGameState;
  });

  useEffect(() => {
    function gameLoop(timestamp) {
      if (lastFrameTimeRef.current === null) { lastFrameTimeRef.current = timestamp; }
      const deltaMs = Math.min(timestamp - lastFrameTimeRef.current, 40);
      lastFrameTimeRef.current = timestamp;
      if (isAuthoritative) {
        if (ballsMovingRef.current) { updatePhysicsRef.current(deltaMs); checkPocketsRef.current(); }
        syncGameStateRef.current();
      }
      if (renderRef.current) renderRef.current();
      animationFrameRef.current = requestAnimationFrame(gameLoop);
    }
    animationFrameRef.current = requestAnimationFrame(gameLoop);
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      lastFrameTimeRef.current = null;
    };
  }, [isAuthoritative, roomId]);

  useEffect(() => {
    if (!isAuthoritative) return;
    if (!ballsMoving) {
      if (shotInProgressRef.current && !shotResolvedRef.current) {
        const objCount = countActiveObjectBalls(ballsRef.current);
        console.log('[Snooker] Object balls on table after shot:', objCount, '| phase:', gamePhaseRef.current, '| redsRemaining:', redsRemainingRef.current);

        if (gamePhaseRef.current !== 'GAME_OVER' && objCount === 0) {
          checkGameEnd();
        }
        if (gamePhaseRef.current === 'GAME_OVER') {
          shotResolvedRef.current = true;
          updateGameState({ shotInProgress: false });
          resetPerShotRuleRefs();
          return;
        }

        const phaseAtShotStart = gamePhaseAtShotStartRef.current;
        const requiredAtShotStart = nextRequiredBallAtShotStartRef.current;
        const contact = effectiveFirstObjectBallType(firstContactRef.current, physicsFirstObjectTypeRef.current);
        const striker = shooterPlayerNumberAtShotStartRef.current ?? currentPlayerRef.current;

        console.log(
          '[Snooker] Shot resolution —',
          'phaseAtShotStart:', phaseAtShotStart,
          '| livePhase:', gamePhaseRef.current,
          '| requiredAtShotStart:', requiredAtShotStart,
          '| firstContact:', contact,
          '| potted:', pottedThisShotRef.current,
          '| foulAlready:', foulThisShotRef.current,
          '| redsRemaining:', redsRemainingRef.current,
          '| redPotsThisShot:', redPotsThisShotRef.current,
          '| striker:', striker
        );

        // Diagnostic for the "potted a red but got a foul" report: a RED-phase shot
        // that touched a red yet registered no pot. If this fires while a red
        // visibly dropped, the pot was lost before processPottedBalls ran.
        if (
          (phaseAtShotStart ?? gamePhaseRef.current) === 'RED' &&
          !pottedThisShotRef.current &&
          !foulThisShotRef.current &&
          contact === 'RED'
        ) {
          console.warn(
            '[Snooker] RED-phase shot hit a red but no pot was registered.',
            'If a red visually dropped, the pot was missed (tunneling) rather than fouled.',
            '| reds on table:', countActiveObjectBalls(ballsRef.current)
          );
        }

        shotResolvedRef.current = true;
        updateGameState({ shotInProgress: false });

        const phaseForMiss = phaseAtShotStart ?? gamePhaseRef.current;
        const requiredForMiss = requiredAtShotStart ?? nextRequiredBallRef.current;
        let contactFoulPts = null;
        if (!pottedThisShotRef.current && !foulThisShotRef.current) {
          if (process.env.NODE_ENV === 'development' && phaseAtShotStart == null) {
            console.warn('[Snooker] gamePhaseAtShotStartRef was null at shot resolution; using live phase for miss fouls');
          }
          contactFoulPts = contactFoulPointsOnMiss(phaseForMiss, requiredForMiss, contact, nominatedColorTypeRef.current);
        }

        if (contactFoulPts != null) {
          handleFoul(contactFoulPts, {
            foulingPlayer: striker,
            reason: foulReasonForMiss(phaseForMiss, requiredForMiss, contact, nominatedColorTypeRef.current) || 'That shot broke the rules! Your opponent gets the points.'
          });
          scheduleSwitchPlayer(200);
        } else if (foulThisShotRef.current) {
          scheduleSwitchPlayer(200);
        } else {
          const legalMiss = !pottedThisShotRef.current;
          const legallyPotted = pottedThisShotRef.current;
          if (legalMiss) {
            endTurn();
          } else if (legallyPotted) {
            updateGameState({ turnContinues: true });
            resetShotTimer();
          }
        }

        resetPerShotRuleRefs();
        shotResolvedRef.current = false;
      }
    }
  }, [ballsMoving, isAuthoritative, updateGameState, endTurn, handleFoul, resetShotTimer, checkGameEnd, scheduleSwitchPlayer, resetPerShotRuleRefs]);

  // Computer mode: persist state to same rooms/ path when balls stop
  const lastComputerSyncMovingRef = useRef(true);
  useEffect(() => {
    if (gameMode !== 'computer' || !roomId || !rtdb) return;
    if (ballsMoving) {
      lastComputerSyncMovingRef.current = true;
      return;
    }
    if (!lastComputerSyncMovingRef.current) return;
    lastComputerSyncMovingRef.current = false;
    const state = buildGameState();
    update(ref(rtdb, `rooms/${roomId}`), { gameState: state, updatedAt: serverTimestamp() }).catch(err => console.error('Snooker computer sync:', err));
  }, [gameMode, roomId, ballsMoving, buildGameState]);

  // Pointer handling (mouse + touch)
  function clearAimState() {
    isAimingRef.current = false;
    aimStartRef.current = null;
    aimCurrentRef.current = null;
    pointerRectRef.current = null;
  }

  function handlePointerDown(e) {
    if (!e.isPrimary) return;
    if (ballsMovingRef.current || shotInProgressRef.current || !isLocalTurn) return;
    if (gameMode === 'computer' && currentPlayerRef.current === 2) return;
    e.preventDefault();
    const canvas = inputCanvasRef.current;
    if (!canvas) return;
    if (canvas.setPointerCapture) canvas.setPointerCapture(e.pointerId);
    const rect = canvas.getBoundingClientRect();
    // Cached for the duration of this drag — handlePointerMove reuses it
    // instead of forcing a layout read on every move event.
    pointerRectRef.current = rect;
    // The input canvas is table + CUE_OVERLAY_MARGIN on every side, so subtract
    // the margin to land back in table coordinates (same space balls/pockets use).
    const x = (e.clientX - rect.left) * (canvas.width / rect.width) - CUE_OVERLAY_MARGIN;
    const y = (e.clientY - rect.top) * (canvas.height / rect.height) - CUE_OVERLAY_MARGIN;
    const whiteBall = ballsRef.current.find(b => b.type === 'WHITE' && b.active);
    if (!whiteBall) return;
    const distance = Math.sqrt(Math.pow(x - whiteBall.x, 2) + Math.pow(y - whiteBall.y, 2));
    if (distance < BALL_RADIUS * 4) {
      isAimingRef.current = true;
      const newAim = { x, y };
      aimStartRef.current = newAim;
      aimCurrentRef.current = newAim;
    }
  }

  function handlePointerMove(e) {
    if (!isLocalTurn || (gameMode === 'computer' && currentPlayerRef.current === 2)) {
      if (isAimingRef.current) clearAimState();
      return;
    }
    if (!isAimingRef.current) return;
    e.preventDefault();
    const canvas = inputCanvasRef.current;
    if (!canvas) return;
    const rect = pointerRectRef.current || canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width) - CUE_OVERLAY_MARGIN;
    const y = (e.clientY - rect.top) * (canvas.height / rect.height) - CUE_OVERLAY_MARGIN;
    // Ref-only write: render() picks this up on the next animation frame.
    // No setState here — that was the source of a full re-render per pointer
    // move, which made the stick visibly lag behind the finger/cursor.
    aimCurrentRef.current = { x, y };
  }

  function handlePointerUp(e) {
    if (!e.isPrimary) return;
    if (inputCanvasRef.current?.releasePointerCapture) {
      inputCanvasRef.current.releasePointerCapture(e.pointerId);
    }
    if (!isLocalTurn || (gameMode === 'computer' && currentPlayerRef.current === 2)) {
      clearAimState();
      return;
    }
    if (!isAimingRef.current) return;
    e.preventDefault();
    isAimingRef.current = false;
    const whiteBall = ballsRef.current.find(b => b.type === 'WHITE' && b.active);
    const currentAim = aimCurrentRef.current;
    if (!whiteBall || !currentAim) { pointerRectRef.current = null; return; }
    const dx = whiteBall.x - currentAim.x; const dy = whiteBall.y - currentAim.y;
    const power = Math.sqrt(dx * dx + dy * dy) / 25;
    if (power > 0.5) {
      resetPerShotRuleRefs();
      gamePhaseAtShotStartRef.current = gamePhaseRef.current;
      nextRequiredBallAtShotStartRef.current = nextRequiredBallRef.current;
      shooterPlayerNumberAtShotStartRef.current = currentPlayerRef.current;
      scoresAtShotStartRef.current = { player1: scoresRef.current.player1, player2: scoresRef.current.player2 };
      shotResolvedRef.current = false;
      const angle = Math.atan2(dy, dx);
      const vx = Math.cos(angle) * power * 10; const vy = Math.sin(angle) * power * 10;
      if (roomId && !isHost) { sendPendingShot(vx, vy); }
      else {
        {
          const wx = vx * CUE_BALL_VELOCITY_SCALE;
          const wy = vy * CUE_BALL_VELOCITY_SCALE;
          const vlen = Math.hypot(wx, wy);
          cueUnitAtShotStartRef.current = vlen > 1e-8 ? { ux: wx / vlen, uy: wy / vlen } : null;
        }
        shotStartWhitePosRef.current = { x: whiteBall.x, y: whiteBall.y };
        lockFirstContactFromShotStart(
          shotStartWhitePosRef.current,
          cueUnitAtShotStartRef.current,
          ballsRef.current,
          gamePhaseAtShotStartRef.current
        );
        const newBalls = ballsRef.current.map(ball => {
          if (ball.type === 'WHITE' && ball.active) { return { ...ball, vx: vx * CUE_BALL_VELOCITY_SCALE, vy: vy * CUE_BALL_VELOCITY_SCALE }; }
          return ball;
        });
        updateGameState({ balls: newBalls, shotInProgress: true, ballsMoving: true });
      }
    }
    aimStartRef.current = null;
    aimCurrentRef.current = null;
    pointerRectRef.current = null;
  }

  function handlePointerCancel(e) {
    if (inputCanvasRef.current?.releasePointerCapture) {
      inputCanvasRef.current.releasePointerCapture(e.pointerId);
    }
    clearAimState();
  }

  // Rendering
  function render() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const useTrails = ballsMovingRef.current;
    if (useTrails) {
      ctx.fillStyle = 'rgba(11, 93, 30, 0.25)';
      ctx.fillRect(0, 0, TABLE_WIDTH, TABLE_HEIGHT);
    } else {
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, 0, TABLE_WIDTH, TABLE_HEIGHT);
    }
    ctx.save();
    ctx.fillStyle = '#1a0f04';
    ctx.beginPath();
    ctx.rect(0, 0, TABLE_WIDTH, TABLE_HEIGHT);
    ctx.rect(FRAME_WIDTH, FRAME_WIDTH, TABLE_WIDTH - 2 * FRAME_WIDTH, TABLE_HEIGHT - 2 * FRAME_WIDTH);
    ctx.fill('evenodd');
    ctx.fillStyle = COLORS.CUSHION;
    ctx.beginPath();
    ctx.rect(FRAME_WIDTH - 1, FRAME_WIDTH - 1, TABLE_WIDTH - 2 * FRAME_WIDTH + 2, TABLE_HEIGHT - 2 * FRAME_WIDTH + 2);
    ctx.rect(INNER_EDGE, INNER_EDGE, TABLE_WIDTH - 2 * INNER_EDGE, TABLE_HEIGHT - 2 * INNER_EDGE);
    ctx.fill('evenodd');
    const feltGradient = ctx.createLinearGradient(INNER_EDGE, INNER_EDGE, INNER_EDGE, TABLE_HEIGHT - INNER_EDGE);
    feltGradient.addColorStop(0, '#0f6a27');
    feltGradient.addColorStop(0.5, '#0b5d1e');
    feltGradient.addColorStop(1, '#084618');
    ctx.fillStyle = feltGradient;
    ctx.fillRect(INNER_EDGE, INNER_EDGE, TABLE_WIDTH - 2 * INNER_EDGE, TABLE_HEIGHT - 2 * INNER_EDGE);
    ctx.save();
    ctx.rect(INNER_EDGE, INNER_EDGE, TABLE_WIDTH - 2 * INNER_EDGE, TABLE_HEIGHT - 2 * INNER_EDGE);
    ctx.clip();
    ctx.globalAlpha = useTrails ? 0.05 : 0.08;
    ctx.strokeStyle = '#117a2e';
    ctx.lineWidth = 18;
    for (let y = INNER_EDGE; y < TABLE_HEIGHT - INNER_EDGE + 40; y += 40) {
      ctx.beginPath();
      ctx.moveTo(INNER_EDGE, y);
      ctx.lineTo(TABLE_WIDTH - INNER_EDGE, y);
      ctx.stroke();
    }
    ctx.restore();
    // Snooker table markings (baulk line + right-facing D), drawn below balls.
    const baulkX = SPOTS.BROWN.x;
    const baulkY1 = INNER_EDGE;
    const baulkY2 = TABLE_HEIGHT - INNER_EDGE;
    const dCenterX = SPOTS.BROWN.x;
    const dCenterY = (SPOTS.GREEN.y + SPOTS.BROWN.y) / 2;
    const dRadius = Math.abs(SPOTS.BROWN.y - SPOTS.GREEN.y) / 2;
    ctx.save();
    ctx.strokeStyle = TABLE_MARKING_COLOR;
    ctx.lineWidth = TABLE_MARKING_WIDTH;
    ctx.beginPath();
    ctx.moveTo(baulkX, baulkY1);
    ctx.lineTo(baulkX, baulkY2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(dCenterX, dCenterY, dRadius, Math.PI / 2, -Math.PI / 2);
    ctx.stroke();
    ctx.restore();
    ctx.fillStyle = '#050505';
    POCKETS.forEach(pocket => { ctx.beginPath(); ctx.arc(pocket.x, pocket.y, POCKET_RADIUS, 0, Math.PI * 2); ctx.fill(); });
    const currentBalls = ballsRef.current;
    const whiteBall = currentBalls.find(ball => ball.type === 'WHITE');
    currentBalls.forEach(ball => {
      if (!ball.active) return;
      const speed = Math.sqrt(ball.vx * ball.vx + ball.vy * ball.vy);
      const highlightOffset = Math.min(speed * 1.5, BALL_RADIUS * 0.6);
      const dirX = speed ? ball.vx / speed : -0.5; const dirY = speed ? ball.vy / speed : -0.5;
      const highlightX = ball.x - dirX * highlightOffset - BALL_RADIUS * 0.3;
      const highlightY = ball.y - dirY * highlightOffset - BALL_RADIUS * 0.3;
      ctx.save(); ctx.shadowColor = 'rgba(0, 0, 0, 0.35)'; ctx.shadowBlur = 6; ctx.shadowOffsetY = 2;
      const gradient = ctx.createRadialGradient(highlightX, highlightY, BALL_RADIUS * 0.2, ball.x, ball.y, BALL_RADIUS);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.85)'); gradient.addColorStop(0.4, ball.color); gradient.addColorStop(1, ball.color);
      ctx.fillStyle = gradient; ctx.beginPath(); ctx.arc(ball.x, ball.y, BALL_RADIUS, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = ball.type === 'WHITE' ? '#444' : 'rgba(0, 0, 0, 0.6)'; ctx.lineWidth = 1; ctx.stroke();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.45)'; ctx.beginPath(); ctx.arc(highlightX, highlightY, BALL_RADIUS * 0.18, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    });
    const showStick = !ballsMovingRef.current && whiteBall && whiteBall.active && gamePhaseRef.current !== 'GAME_OVER';
    const currentAim = aimCurrentRef.current;
    const aimStartNow = aimStartRef.current;
    let aimPoint = null;
    if (showStick && whiteBall) {
      if (isLocalTurn && isAimingRef.current && aimStartNow && currentAim) {
        aimPoint = currentAim;
      } else if (displayAimRef.current) {
        aimPoint = displayAimRef.current;
      } else {
        const toCenterX = TABLE_WIDTH / 2 - whiteBall.x;
        const toCenterY = TABLE_HEIGHT / 2 - whiteBall.y;
        const len = Math.sqrt(toCenterX * toCenterX + toCenterY * toCenterY) || 1;
        const restPull = 50;
        aimPoint = { x: whiteBall.x - (toCenterX / len) * restPull, y: whiteBall.y - (toCenterY / len) * restPull };
      }
    }
    if (showStick && whiteBall && aimPoint) {
      drawCueStickOnOverlay(whiteBall, aimPoint);
    } else {
      clearCueOverlay();
    }
    if (isLocalTurn && isAimingRef.current && aimStartNow && currentAim && whiteBall && whiteBall.active) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)'; ctx.lineWidth = 1; ctx.setLineDash([5, 5]);
      ctx.beginPath(); ctx.moveTo(whiteBall.x, whiteBall.y);
      const dx = whiteBall.x - currentAim.x; const dy = whiteBall.y - currentAim.y;
      const angle = Math.atan2(dy, dx);
      ctx.lineTo(whiteBall.x + Math.cos(angle) * 400, whiteBall.y + Math.sin(angle) * 400);
      ctx.stroke(); ctx.setLineDash([]);
      const power = Math.sqrt(dx * dx + dy * dy) / 25;
      drawPowerMeter(ctx, whiteBall, power);
    }
    ctx.restore();
  }
  renderRef.current = render;

  /** Visual power gauge shown beside the cue ball while aiming — a filled bar plus the numeric readout. */
  function drawPowerMeter(ctx, whiteBall, power) {
    const clamped = Math.min(Math.max(power, 0), MAX_SHOT_POWER);
    const ratio = clamped / MAX_SHOT_POWER;
    const barWidth = 90;
    const barHeight = 10;
    const anchorX = whiteBall.x + 25;
    const anchorY = whiteBall.y - 46;

    ctx.save();
    // Track
    ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.lineWidth = 1.5;
    roundRectPath(ctx, anchorX, anchorY, barWidth, barHeight, 5);
    ctx.fill();
    ctx.stroke();

    // Fill — green through to red as power approaches the cap, so it's readable at a glance mid-drag.
    const fillWidth = Math.max(2, barWidth * ratio);
    const fillGradient = ctx.createLinearGradient(anchorX, 0, anchorX + barWidth, 0);
    fillGradient.addColorStop(0, '#32d083');
    fillGradient.addColorStop(0.55, '#ffd23f');
    fillGradient.addColorStop(1, '#ff3b3b');
    ctx.save();
    roundRectPath(ctx, anchorX, anchorY, barWidth, barHeight, 5);
    ctx.clip();
    ctx.fillStyle = fillGradient;
    ctx.fillRect(anchorX, anchorY, fillWidth, barHeight);
    ctx.restore();

    // A soft-shot drag (below MIN_SHOT_POWER) won't actually fire — hint that visually.
    if (power < MIN_SHOT_POWER) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.font = '10px Arial';
      ctx.fillText('too soft', anchorX, anchorY - 4);
    }

    ctx.fillStyle = '#FFF';
    ctx.font = 'bold 16px Arial';
    ctx.fillText(`Power: ${clamped.toFixed(1)}`, anchorX, anchorY + barHeight + 20);
    ctx.restore();
  }

  function roundRectPath(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  function clearCueOverlay() {
    const cueCanvas = cueCanvasRef.current;
    if (!cueCanvas) return;
    const cueCtx = cueCanvas.getContext('2d');
    cueCtx.clearRect(0, 0, cueCanvas.width, cueCanvas.height);
  }

  function distanceToCanvasEdge(x, y, dirX, dirY, canvasW, canvasH) {
    let t = Infinity;
    if (dirX > 1e-6) t = Math.min(t, (canvasW - x) / dirX);
    if (dirX < -1e-6) t = Math.min(t, -x / dirX);
    if (dirY > 1e-6) t = Math.min(t, (canvasH - y) / dirY);
    if (dirY < -1e-6) t = Math.min(t, -y / dirY);
    return Number.isFinite(t) ? Math.max(0, t) : 0;
  }

  function drawCueStickShape(ctx, whiteBall, aimCurrent) {
    const dx = whiteBall.x - aimCurrent.x; const dy = whiteBall.y - aimCurrent.y;
    const angle = Math.atan2(dy, dx);
    const distance = Math.sqrt(dx * dx + dy * dy);
    let pullBack = Math.min(distance, 150);
    const stickWidth = 10; const tipWidth = 5;
    const buttDirX = Math.cos(angle + Math.PI);
    const buttDirY = Math.sin(angle + Math.PI);
    const canvasW = ctx.canvas?.width || TABLE_WIDTH + CUE_OVERLAY_MARGIN * 2;
    const canvasH = ctx.canvas?.height || TABLE_HEIGHT + CUE_OVERLAY_MARGIN * 2;
    const availableLength = distanceToCanvasEdge(whiteBall.x, whiteBall.y, buttDirX, buttDirY, canvasW, canvasH);

    // Near a rail or pocket there may not be room for the full pull-back plus a
    // visible stick. Previously the stick length floored at 72px regardless of
    // how little space was left, so it was drawn past the canvas edge — the
    // "stick goes off screen" issue. Instead, shrink the pull-back distance
    // first (how far the stick's gap from the ball extends) so the whole stick,
    // including its minimum visible length, always fits in the available space.
    // This only changes the drawn stick — the actual shot power still comes
    // from the real drag distance in handlePointerUp, untouched here.
    const minVisibleStick = CUE_STICK_MIN_LENGTH + 8;
    const maxPullBackForSpace = availableLength - BALL_RADIUS - 5 - minVisibleStick;
    if (maxPullBackForSpace < pullBack) {
      pullBack = Math.max(0, maxPullBackForSpace);
    }

    ctx.save(); ctx.translate(whiteBall.x, whiteBall.y); ctx.rotate(angle + Math.PI);
    const gap = BALL_RADIUS + 5 + pullBack;
    // Even with pull-back at zero, extremely tight corners may leave less room
    // than CUE_STICK_MIN_LENGTH. Shrink the stick itself in that case too, so
    // it is always contained — a shorter stick is preferable to one clipped
    // off the edge of the canvas.
    const remainingForStick = Math.max(0, availableLength - gap - 8);
    const stickLength = Math.min(CUE_STICK_MAX_LENGTH, remainingForStick >= CUE_STICK_MIN_LENGTH
      ? remainingForStick
      : Math.max(4, remainingForStick));
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)'; ctx.shadowBlur = 12; ctx.shadowOffsetY = 8;
    const stickGradient = ctx.createLinearGradient(gap, 0, gap + stickLength, 0);
    stickGradient.addColorStop(0, '#f0d9b5'); stickGradient.addColorStop(0.05, '#8b4513');
    stickGradient.addColorStop(0.8, '#4b2d0b'); stickGradient.addColorStop(1, '#1a0f04');
    ctx.fillStyle = stickGradient;
    ctx.beginPath(); ctx.moveTo(gap, -tipWidth / 2); ctx.lineTo(gap + stickLength, -stickWidth / 2);
    ctx.lineTo(gap + stickLength, stickWidth / 2); ctx.lineTo(gap, tipWidth / 2); ctx.closePath(); ctx.fill();
    ctx.fillStyle = '#4a90e2'; ctx.fillRect(gap, -tipWidth / 2, 4, tipWidth);
    ctx.fillStyle = '#ffffff'; ctx.fillRect(gap + 4, -tipWidth / 2, 8, tipWidth);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)'; ctx.lineWidth = 1;
    for (let i = 0; i < 5; i++) {
      ctx.beginPath(); ctx.moveTo(gap + stickLength - 100 + i * 15, -stickWidth / 2);
      ctx.lineTo(gap + stickLength - 100 + i * 15, stickWidth / 2); ctx.stroke();
    }
    ctx.restore();
  }

  function drawCueStickOnOverlay(whiteBall, aimCurrent) {
    const cueCanvas = cueCanvasRef.current;
    if (!cueCanvas) return;
    const cueCtx = cueCanvas.getContext('2d');
    cueCtx.clearRect(0, 0, cueCanvas.width, cueCanvas.height);
    const offsetWhite = { x: whiteBall.x + CUE_OVERLAY_MARGIN, y: whiteBall.y + CUE_OVERLAY_MARGIN };
    const offsetAim = { x: aimCurrent.x + CUE_OVERLAY_MARGIN, y: aimCurrent.y + CUE_OVERLAY_MARGIN };
    drawCueStickShape(cueCtx, offsetWhite, offsetAim);
  }

  const hostOnline = isPresenceFresh(presence.hostLastSeen);
  const guestOnline = isPresenceFresh(presence.guestLastSeen);
  let overlayMessage = '';
  if (roomId && !isTournamentMatch) {
    if (gameMode === 'multiplayer') {
      if (isHost && !guestId) {
        overlayMessage = 'Waiting for opponent to join...';
      } else if (!isHost && !hostOnline) overlayMessage = 'Host disconnected';
      else if (isHost && guestId && !guestOnline) overlayMessage = 'Guest disconnected';
    }
  }

  // -------- Tournament waiting lobby (no table until gameStarted) --------
  if (gameMode === 'multiplayer' && roomId && !gameStarted && isTournamentMatch) {
    const lobbyPlayers = [
      { key: 'host', name: player1Name, online: hostOnline, label: 'Player 1' },
    ];
    if (guestId) {
      lobbyPlayers.push({ key: 'guest', name: player2Name, online: guestOnline, label: 'Player 2' });
    }
    return (
      <div className="snooker-landing snooker-waiting-lobby">
        <div className="snooker-waiting-lobby__card">
          <h1 className="snooker-waiting-lobby__title">Waiting for users…</h1>
          <p className="snooker-waiting-lobby__subtitle">vs {opponentDisplayName}</p>
          {!guestId && (
            <p className="snooker-waiting-lobby__timer">
              Time remaining:{' '}
              {String(Math.floor(lobbyTimerSeconds / 60)).padStart(2, '0')}:
              {String(lobbyTimerSeconds % 60).padStart(2, '0')}
            </p>
          )}
          <div className="snooker-waiting-lobby__players">
            {lobbyPlayers.map((p) => (
              <div key={p.key} className={`snooker-waiting-lobby__player ${p.online ? 'snooker-waiting-lobby__player--on' : ''}`}>
                <span className="snooker-waiting-lobby__player-name">{p.name}</span>
                <span className={`presence ${p.online ? 'presence--on' : 'presence--off'}`}>
                  {p.online ? 'Online' : 'Offline'}
                </span>
              </div>
            ))}
          </div>
          {!guestId && (
            <p className="snooker-waiting-lobby__hint">Waiting for your opponent to join…</p>
          )}
          {guestId && (
            <p className="snooker-waiting-lobby__hint">Both players joined — starting match…</p>
          )}
          {lobbyTimeout || gameOverModal ? (
            <button type="button" className="btn btn--ghost snooker-waiting-lobby__back" onClick={handleBackAfterMatchEnd}>
              Back
            </button>
          ) : (
            <button type="button" className="btn btn--ghost snooker-waiting-lobby__back" onClick={handleTournamentLeaveClick}>
              Leave game
            </button>
          )}
          {roomError && <p className="snooker-waiting-lobby__error">{roomError}</p>}
        </div>
        {leaveConfirmModal && (
          <div className="game-over-modal-overlay" role="presentation" onClick={() => setLeaveConfirmModal(null)}>
            <div
              role="dialog"
              aria-modal="true"
              className="game-over-modal game-over-modal--loss"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="game-over-modal__title">Leave waiting room?</h2>
              <p className="game-over-modal__body">
                You will return to the tournament bracket. The match has not started yet.
              </p>
              <div className="leave-confirm-modal__actions">
                <button type="button" className="game-over-modal__ok leave-confirm-modal__cancel" onClick={() => setLeaveConfirmModal(null)}>
                  Stay in game
                </button>
                <button type="button" className="game-over-modal__ok leave-confirm-modal__confirm" onClick={confirmTournamentLeave}>
                  Leave game
                </button>
              </div>
            </div>
          </div>
        )}
        {gameOverModal && (
          <div className="game-over-modal-overlay" role="presentation">
            <div role="dialog" aria-modal="true" className={`game-over-modal game-over-modal--${gameOverModal.variant}`}>
              <h2 className="game-over-modal__title">{gameOverModal.title}</h2>
              <p className="game-over-modal__score">{gameOverModal.scoreLine}</p>
              <p className="game-over-modal__body">{gameOverModal.body}</p>
              <button type="button" className="game-over-modal__ok" onClick={handleGameOverModalOk}>OK</button>
            </div>
          </div>
        )}
        {!guestId && !leaveConfirmModal && !gameOverModal && (
          <SnookerWaitingOverlay secondsLeft={lobbyTimerSeconds} />
        )}
      </div>
    );
  }

  // -------- Landing screen (mandatory unless rejoin) --------
  if (showLanding) {
    return (
      <div className="snooker-landing" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: '20px',
        fontFamily: "'Segoe UI', sans-serif",
      }}>
        <div className="snooker-landing-card" style={{
          background: 'white', borderRadius: '22px', padding: '36px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          textAlign: 'center', maxWidth: '420px', width: '100%',
        }}>
          <h1 style={{ marginBottom: '8px', color: '#0b5d1e', fontSize: '28px' }}>Snooker Mania</h1>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '24px' }}>Choose game mode</p>

          <div style={{ marginBottom: '22px' }}>
            <label style={{ display: 'block', fontWeight: 600, color: '#333', marginBottom: '8px', textAlign: 'left' }}>Game Mode</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              {(!isLiveMode || liveThemeFlags.multiplayer) && (
                <button
                  type="button"
                  onClick={() => setGameMode('multiplayer')}
                  style={{
                    flex: 1, padding: '12px 16px', border: gameMode === 'multiplayer' ? '2px solid #0b5d1e' : '2px solid #e7e7e7',
                    borderRadius: '12px', fontSize: '14px', cursor: 'pointer',
                    background: gameMode === 'multiplayer' ? '#e8f5e9' : 'white', color: gameMode === 'multiplayer' ? '#0b5d1e' : '#666',
                    fontWeight: gameMode === 'multiplayer' ? 600 : 400,
                  }}
                >
                  Multiplayer
                </button>
              )}
              {(!isLiveMode || liveThemeFlags.computer) && (
                <button
                  type="button"
                  onClick={() => setGameMode('computer')}
                  style={{
                    flex: 1, padding: '12px 16px', border: gameMode === 'computer' ? '2px solid #0b5d1e' : '2px solid #e7e7e7',
                    borderRadius: '12px', fontSize: '14px', cursor: 'pointer',
                    background: gameMode === 'computer' ? '#e8f5e9' : 'white', color: gameMode === 'computer' ? '#0b5d1e' : '#666',
                    fontWeight: gameMode === 'computer' ? 600 : 400,
                  }}
                >
                  Computer
                </button>
              )}
            </div>
          </div>

          {gameMode === 'computer' && (
            <button
              type="button"
              onClick={async () => {
                setGameMode('computer');
                setShowLanding(false);
                resetMatchToInitialState(userName || 'Player 1');
                gameOverNotifiedRef.current = false;
                try {
                  const clientId = clientIdRef.current;
                  if (!clientId || !rtdb) return;
                  setRoomError('');
                  setRoomStatus('connecting');
                  for (let attempt = 0; attempt < 5; attempt += 1) {
                    const code = generateRoomCode();
                    const roomRef = ref(rtdb, `rooms/${code}`);
                    const snapshot = await get(roomRef);
                    if (snapshot.exists()) continue;
                    const initialState = buildGameState();
                    const hostDisplayName = (() => { try { const s = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null; return (s ? JSON.parse(s) : {}).name || 'Player 1'; } catch (e) { return 'Player 1'; } })();
                    await set(roomRef, {
                      code,
                      hostId: clientId,
                      guestId: null,
                      hostName: hostDisplayName,
                      gameMode: 'computer',
                      gameState: initialState,
                      createdAt: serverTimestamp(),
                      updatedAt: serverTimestamp(),
                      hostLastSeen: serverTimestamp(),
                      guestLastSeen: null,
                    });
                    setRoomId(code);
                    setRoomCode(code);
                    setIsHost(true);
                    setRoomCodeInput('');
                    setRoomStatus('connected');
                    setShowLanding(false);
                    setGameMode('computer');
                    persistRoomCode(code);
                    await notifySnookerLiveGameStart(code, [
                      { userId: typeof sessionStorage !== 'undefined' && sessionStorage.getItem('userData') ? JSON.parse(sessionStorage.getItem('userData')).userId : null, name: hostDisplayName },
                      { name: 'Computer' },
                    ]);
                    return;
                  }
                  setRoomError('Unable to create a room. Please try again.');
                  setRoomStatus('offline');
                } catch (err) {
                  console.error('Snooker computer game init:', err);
                  setRoomStatus('offline');
                }
              }}
              style={{
                width: '100%', padding: '14px 22px', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 600,
                cursor: 'pointer', background: 'linear-gradient(135deg, #0b5d1e 0%, #084618 100%)', color: 'white',
                marginBottom: '18px', boxShadow: '0 10px 25px rgba(11, 93, 30, 0.35)',
              }}
            >
              Play
            </button>
          )}

          {gameMode === 'multiplayer' && (
            <>
              <button
                type="button"
                onClick={createRoom}
                disabled={roomStatus === 'connecting'}
                style={{
                  width: '100%', padding: '14px 22px', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 600,
                  cursor: roomStatus === 'connecting' ? 'not-allowed' : 'pointer',
                  background: roomStatus === 'connecting' ? '#ccc' : 'linear-gradient(135deg, #0b5d1e 0%, #084618 100%)',
                  color: 'white', marginBottom: '12px', boxShadow: '0 10px 25px rgba(11, 93, 30, 0.35)',
                }}
              >
                {roomStatus === 'connecting' ? 'Creating...' : 'Create game'}
              </button>
              <div style={{ margin: '24px 0', display: 'flex', alignItems: 'center', color: '#999', fontWeight: 600 }}>
                <div style={{ flex: 1, height: '2px', background: '#e0e0e0' }} />
                <span style={{ padding: '0 15px' }}>OR</span>
                <div style={{ flex: 1, height: '2px', background: '#e0e0e0' }} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 600, color: '#333', marginBottom: '8px', textAlign: 'left' }}>Room code</label>
                <input
                  type="text"
                  value={roomCodeInput}
                  onChange={(e) => setRoomCodeInput(e.target.value.toUpperCase().slice(0, ROOM_CODE_LENGTH))}
                  placeholder="Enter 6-character code"
                  maxLength={ROOM_CODE_LENGTH}
                  disabled={roomStatus === 'connecting'}
                  style={{
                    width: '100%', padding: '12px 16px', border: '2px solid #e7e7e7', borderRadius: '12px', fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <button
                type="button"
                onClick={joinRoom}
                disabled={roomStatus === 'connecting' || !roomCodeInput.trim()}
                style={{
                  width: '100%', padding: '14px 22px', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 600,
                  cursor: roomStatus === 'connecting' || !roomCodeInput.trim() ? 'not-allowed' : 'pointer',
                  background: roomStatus === 'connecting' || !roomCodeInput.trim() ? '#ccc' : '#2e7d32', color: 'white',
                  boxShadow: roomStatus === 'connecting' || !roomCodeInput.trim() ? 'none' : '0 10px 25px rgba(46, 125, 50, 0.35)',
                }}
              >
                Join game
              </button>
              {roomError && <p style={{ marginTop: '12px', color: '#c62828', fontSize: '14px' }}>{roomError}</p>}
            </>
          )}
        </div>
      </div>
    );
  }

  // -------- Lobby (casual multiplayer only) --------
  if (gameMode === 'multiplayer' && roomId && !gameStarted && !isTournamentMatch) {
    return (
      <div className="snooker-landing" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: '20px', fontFamily: "'Segoe UI', sans-serif" }}>
        <div className="snooker-landing-card" style={{ background: 'white', borderRadius: '22px', padding: '36px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', textAlign: 'center', maxWidth: '420px', width: '100%' }}>
          <h1 style={{ marginBottom: '8px', color: '#0b5d1e', fontSize: '28px' }}>Room lobby</h1>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>Room: <strong>{roomCode}</strong></p>
          <p style={{ fontSize: '13px', color: '#888', marginBottom: '20px' }}>Share this code with a friend</p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '20px' }}>
            <span className={`presence ${hostOnline ? 'presence--on' : 'presence--off'}`}>Host {hostOnline ? 'online' : 'offline'}</span>
            <span className={`presence ${guestOnline ? 'presence--on' : 'presence--off'}`}>Guest {guestOnline ? 'online' : 'offline'}</span>
          </div>
          {!guestId && <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>Waiting for opponent to join...</p>}
          <div className="lobby-actions" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button type="button" className="btn btn--primary lobby-btn lobby-btn--start" onClick={startGame} disabled={!guestId} style={{ width: '100%', padding: '14px 22px', fontSize: '16px', fontWeight: 600 }}>
              Start game
            </button>
            <button type="button" className="btn btn--ghost lobby-btn lobby-btn--leave" onClick={leaveRoom} style={{ width: '100%', padding: '12px' }}>Leave room</button>
          </div>
          {roomError && <p style={{ marginTop: '12px', color: '#c62828', fontSize: '14px' }}>{roomError}</p>}
        </div>
      </div>
    );
  }

  const showTournamentRetryDots =
    isTournamentMatch && gameMode === 'multiplayer' && gameStarted && !showLanding && (roomId || initialRejoinCode);

  const handleNewGame = async () => {
    setGameOverModal(null);
    if (gameMode === 'multiplayer') {
      if (roomId) await leaveRoom({ showLandingNext: false });
      rejoinAttemptedRef.current = false;
      gameOverNotifiedRef.current = false;
      resetMatchToInitialState(userName || 'Player 1');
      await createRoom();
      return;
    }
    if (roomId) await leaveRoom();
    rejoinAttemptedRef.current = false;
    gameOverNotifiedRef.current = false;
    setShowLanding(true);
  };

  const statusPlayerLabel = currentPlayer === 1 ? player1Name : player2Name;
  const liveStatusMessage = `${statusPlayerLabel} to play - ${currentTargetLabel(gamePhase, nextRequiredBall)}`;
  const statusCardMessage = message && (String(message).startsWith('FOUL!') || gamePhase === 'GAME_OVER')
    ? message
    : liveStatusMessage;
  /** Match is decided — leaving can no longer forfeit anything, so offer Back instead of Leave. */
  const matchHasEnded = gamePhase === 'GAME_OVER' || lobbyTimeout || !!gameOverModal;

  return (
    <div className="app">
      <div className="app__left">
        <header className="app__header">
          <div className="brand">
            <div className="brand__row">
              {/* <h1 className="brand__title">Snooker</h1> */}
              {gameMode !== 'computer' && <p className="brand__subtitle">Multiplayer</p>}
              <button
                type="button"
                className="info-btn"
                onClick={() => setShowHowToPlay(true)}
                aria-haspopup="dialog"
                aria-label="How to play"
                title="How to play"
              >
                i
              </button>
            </div>
          </div>
          <div className="app__header-actions">
            {gameMode === 'computer' && roomId && (
              <div className="room-panel room-panel--compact">
                <span className="room-label">Room: {roomCode}</span>
              </div>
            )}
            {!isTournamentMatch && (
              <button type="button" className="btn btn--ghost" onClick={handleNewGame}>
                New game
              </button>
            )}
            {gameMode === 'multiplayer' && isTournamentMatch && !roomId && (
              <button
                type="button"
                className="btn btn--danger"
                onClick={() => {
                  window.location.href = `/tournament-structure/${urlConfig.tournamentId}`;
                }}
              >
                Leave game
              </button>
            )}
            {gameMode === 'multiplayer' && roomId && (
              <div className="room-panel room-panel--compact room-panel--row">
                {!isTournamentMatch && <span className="room-label">Room: {roomCode}</span>}
                {isTournamentMatch && (
                  <span className="room-label">
                    vs {opponentDisplayName}
                  </span>
                )}
                {!isTournamentMatch && (
                  <div className="room-presence">
                    <span className={`presence ${hostOnline ? 'presence--on' : 'presence--off'}`}>Host {hostOnline ? 'online' : 'offline'}</span>
                    <span className={`presence ${guestOnline ? 'presence--on' : 'presence--off'}`}>Guest {guestOnline ? 'online' : 'offline'}</span>
                  </div>
                )}
                {matchHasEnded ? (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={handleBackAfterMatchEnd}
                  >
                    Back
                  </button>
                ) : (
                  <button
                    className="btn btn--danger"
                    onClick={isTournamentMatch ? handleTournamentLeaveClick : leaveRoom}
                  >
                    {isTournamentMatch ? 'Leave game' : 'Leave'}
                  </button>
                )}
              </div>
            )}
          </div>
        </header>
        <section className="hud">
          <div className="scoreboard">
            <div className={`score-card ${currentPlayer === 1 ? 'score-card--active' : ''}`}>
              <span className="score-label">{player1Name}</span>
              {showTournamentRetryDots && (
                <RetryDots missed={missedTurns.player1 ?? 0} />
              )}
              <span className="score-value">{scores.player1}</span>
            </div>
            <div className={`score-card ${currentPlayer === 2 ? 'score-card--active' : ''}`}>
              <span className="score-label">{player2Name}</span>
              {showTournamentRetryDots && (
                <RetryDots missed={missedTurns.player2 ?? 0} />
              )}
              <span className="score-value">{scores.player2}</span>
            </div>
          </div>
          <div className="status-card">{statusCardMessage}</div>
          <div className="info-row">
            <span className="info-chip">Reds Remaining: {redsRemaining}</span>
            <span className="info-chip">Phase: {gamePhase === 'GAME_OVER' ? 'Game Over' : gamePhase === 'RED' ? 'Potting Reds' : gamePhase === 'COLOR' ? 'Potting Color' : 'Colors Only'}</span>
            {gamePhase !== 'GAME_OVER' && <span className="info-chip">On: {currentTargetLabel(gamePhase, nextRequiredBall).replace('Pot ', '').replace('Choose any ', '')}</span>}
            <span
              className={`info-chip shot-timer-chip${
                gamePhase !== 'GAME_OVER' && isLocalTurn && gameStarted ? ' shot-timer-chip--active' : ''
              }${shotTimeLeft <= 10 && gamePhase !== 'GAME_OVER' ? ' shot-timer-chip--urgent' : ''}`}
            >
              <span className="shot-timer-chip__label">Shot Timer</span>
              <span className="shot-timer-chip__value">{shotTimeLeft}s</span>
            </span>
            {roomId && !isTournamentMatch && !isSpectator && (
              <span className="info-chip">You are Player {localPlayerNumber} {isHost ? '(Host)' : '(Guest)'}</span>
            )}
            {roomId && isTournamentMatch && !isSpectator && (
              <span className="info-chip">
                {isHost ? 'You' : opponentDisplayName} vs {isHost ? opponentDisplayName : 'You'}
              </span>
            )}
            {isSpectator && (
              <span className="info-chip spectator-badge">👁 Spectating</span>
            )}
          </div>
        </section>
      </div>
      <div className="table-card">
        {showYourTurnPopup && !foulPopupText && !scorePopupText && (
          <div className="your-turn-popup" role="status" aria-live="polite">
            <div className="your-turn-popup__title">{yourTurnPopupText}</div>
            {currentTargetLabel(gamePhase, nextRequiredBall) && (
              <div className="your-turn-popup__target">{currentTargetLabel(gamePhase, nextRequiredBall)}</div>
            )}
          </div>
        )}
        {foulPopupText && (
          <div key={`${foulPopupText}-${foulPopupReason || ''}`} className="foul-popup" role="alert" aria-live="assertive">
            <div className="foul-popup__title">{foulPopupText}</div>
            {foulPopupReason && <div className="foul-popup__reason">{foulPopupReason}</div>}
          </div>
        )}
        {scorePopupText && (
          <div key={scorePopupText} className={`score-popup${foulPopupText ? ' score-popup--below-foul' : ''}`} role="status" aria-live="polite">{scorePopupText}</div>
        )}
        <div className="table-frame">
          {!isSpectator && (
          <canvas
            ref={cueCanvasRef}
            width={TABLE_WIDTH + CUE_OVERLAY_MARGIN * 2}
            height={TABLE_HEIGHT + CUE_OVERLAY_MARGIN * 2}
            className="cue-overlay-canvas"
            style={{
              inset: `-${CUE_OVERLAY_MARGIN}px`,
              width: `calc(100% + ${CUE_OVERLAY_MARGIN * 2}px)`,
              height: `calc(100% + ${CUE_OVERLAY_MARGIN * 2}px)`,
            }}
            aria-hidden="true"
          />
          )}
          <canvas
            ref={canvasRef}
            width={TABLE_WIDTH}
            height={TABLE_HEIGHT}
            className={`table-canvas ${ballsMoving ? 'table-canvas--disabled' : ''}`}
          />
          {!isSpectator && (
          <canvas
            ref={inputCanvasRef}
            width={TABLE_WIDTH + CUE_OVERLAY_MARGIN * 2}
            height={TABLE_HEIGHT + CUE_OVERLAY_MARGIN * 2}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            className="cue-input-canvas"
            style={{
              inset: `-${CUE_OVERLAY_MARGIN}px`,
              width: `calc(100% + ${CUE_OVERLAY_MARGIN * 2}px)`,
              height: `calc(100% + ${CUE_OVERLAY_MARGIN * 2}px)`,
              pointerEvents:
                isLocalTurn && !ballsMoving && !shotInProgress && gamePhase !== 'GAME_OVER'
                  ? 'auto'
                  : 'none',
            }}
            aria-hidden="true"
          />
          )}
          {overlayMessage && <div className="table-overlay"><div className="overlay-card">{overlayMessage}</div></div>}
        </div>
      </div>
      {leaveConfirmModal && (
        <div className="game-over-modal-overlay" role="presentation" onClick={() => setLeaveConfirmModal(null)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="leave-confirm-modal-title"
            className="game-over-modal game-over-modal--loss"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="leave-confirm-modal-title" className="game-over-modal__title">
              {leaveConfirmModal === 'forfeit' ? 'Leave match?' : 'Leave waiting room?'}
            </h2>
            <p className="game-over-modal__body">
              {leaveConfirmModal === 'forfeit'
                ? `If you leave now, you will forfeit this frame. ${opponentDisplayName} will win by abandonment.`
                : 'You will return to the tournament bracket. The match has not started yet.'}
            </p>
            <div className="leave-confirm-modal__actions">
              <button type="button" className="game-over-modal__ok leave-confirm-modal__cancel" onClick={() => setLeaveConfirmModal(null)}>
                Stay in game
              </button>
              <button type="button" className="game-over-modal__ok leave-confirm-modal__confirm" onClick={confirmTournamentLeave}>
                Leave game
              </button>
            </div>
          </div>
        </div>
      )}
      {gameOverModal && (
        <div className="game-over-modal-overlay" role="presentation">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="game-over-modal-title"
            className={`game-over-modal game-over-modal--${gameOverModal.variant}`}
          >
            <div className="game-over-modal__icon">
              <SnookerGameOverBallIcon />
            </div>
            <h2 id="game-over-modal-title" className="game-over-modal__title">{gameOverModal.title}</h2>
            <p className="game-over-modal__score">{gameOverModal.scoreLine}</p>
            <p className="game-over-modal__body">{gameOverModal.body}</p>
            <button type="button" className="game-over-modal__ok" onClick={handleGameOverModalOk}>OK</button>
          </div>
        </div>
      )}
      {showHowToPlay && (
        <div
          className="how-to-play-overlay"
          role="presentation"
          onClick={() => setShowHowToPlay(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="how-to-play-title"
            className="how-to-play-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="how-to-play-modal__close"
              onClick={() => setShowHowToPlay(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <h2 id="how-to-play-title" className="how-to-play-modal__title">How to Play</h2>
            <p className="how-to-play-modal__tagline">Aim, pocket, and outscore your opponent!</p>

            <ul className="how-to-play-modal__list">
              <li><strong>Aim and shoot:</strong> Click or touch the white ball, drag backwards from the direction you want to shoot, and release. Drag further for a stronger shot. Use the aiming line to guide you.</li>
              <li><strong>Follow your target:</strong> Check the &ldquo;On&rdquo; indicator to see which ball to pocket next.</li>
              <li><strong>Red, then colour:</strong> Pocket a red ball, followed by any one coloured ball. Repeat while reds remain. For a colour shot, hit your chosen colour first and pocket that same ball.</li>
              <li><strong>Watch the balls return:</strong> Pocketed reds stay off the table. Coloured balls return during the red and colour stage.</li>
              <li><strong>Clear the final colours:</strong> After the last red, pocket Yellow → Green → Brown → Blue → Pink → Black in order. These colours now stay off the table.</li>
              <li><strong>Keep your turn:</strong> Pocket the correct ball to play again. If you hit the correct ball but do not pocket it, your opponent plays next.</li>
              <li><strong>Beat the clock:</strong> Take each shot within 30 seconds, or your turn passes to your opponent. Wait for all balls to stop before shooting again.</li>
            </ul>

            <h3 className="how-to-play-modal__subtitle">Scoring</h3>
            <table className="how-to-play-modal__table">
              <thead>
                <tr><th>Ball</th><th>Points</th></tr>
              </thead>
              <tbody>
                <tr><td>Red</td><td>1</td></tr>
                <tr><td>Yellow</td><td>2</td></tr>
                <tr><td>Green</td><td>3</td></tr>
                <tr><td>Brown</td><td>4</td></tr>
                <tr><td>Blue</td><td>5</td></tr>
                <tr><td>Pink</td><td>6</td></tr>
                <tr><td>Black</td><td>7</td></tr>
              </tbody>
            </table>

            <h3 className="how-to-play-modal__subtitle">Avoid Fouls</h3>
            <p className="how-to-play-modal__note">A foul ends your turn and gives your opponent 4 to 7 points. You earn no pocketing points from that shot.</p>
            <ul className="how-to-play-modal__list">
              <li>Do not pocket the white ball. This gives your opponent 4 points, and the white ball returns to the table.</li>
              <li>Do not hit the wrong ball first or miss all balls.</li>
              <li>Do not pocket a colour when a red is required, or a red when a colour is required.</li>
              <li>On a colour shot, pocket the same colour you aimed at and hit first.</li>
              <li>Do not pocket more than one coloured ball in a shot.</li>
              <li>Follow the colour order once all reds are cleared.</li>
            </ul>

            <h3 className="how-to-play-modal__subtitle">Winning</h3>
            <p className="how-to-play-modal__note">When all red and coloured balls are cleared, the player with the higher score wins!</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default SnookerGame;
